import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import TaskDetailsPanel from "./taskdetailpanel";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  IconButton,
  Tooltip,
  LinearProgress,
  Divider,
  Menu,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  TablePagination,
  Stack
} from "@mui/material";
import {
  FolderKanban,
  CheckCircle2,
  AlertTriangle,
  Clock,
  Calendar,
  MoreVertical,
  ArrowUpRight,
  TrendingUp,
  Filter,
  RefreshCw,
  X,
  PieChart as PieChartIcon,
  Sparkles,
  Download,
  Eye
} from "lucide-react";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend
} from "recharts";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";

const CpoPpmDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters
  const [selectedProject, setSelectedProject] = useState("all");
  const [selectedProgram, setSelectedProgram] = useState("all");
  const [selectedMonth, setSelectedMonth] = useState("May 2026");

  // Dashboard state
  const [dashboardData, setDashboardData] = useState(null);
  const [companyConfig, setCompanyConfig] = useState(null);

  // Pagination state for Active Projects Summary table
  const [projectPage, setProjectPage] = useState(0);
  const [projectRowsPerPage, setProjectRowsPerPage] = useState(5);

  const handleChangeProjectPage = (event, newPage) => {
    setProjectPage(newPage);
  };

  const handleChangeProjectRowsPerPage = (event) => {
    setProjectRowsPerPage(parseInt(event.target.value, 10));
    setProjectPage(0);
  };

  // Modal dialog state for Card Details
  const [modalOpen, setModalOpen] = useState(false);
  const [cardModalType, setCardModalType] = useState("total"); // "total", "onTrack", "atRisk", "delayed", "progress", "npi"
  const [modalTab, setModalTab] = useState(0); // 0 = Active, 1 = Completed, 2 = All
  const [selectedNpiStatusFilter, setSelectedNpiStatusFilter] = useState("All");

  // Task Details Panel States & Configuration
  const [taskDetailsPanelOpen, setTaskDetailsPanelOpen] = useState(false);
  const [selectedTaskForPanel, setSelectedTaskForPanel] = useState(null);

  // AI matching & portfolio analysis states
  const [selectedNpiForAI, setSelectedNpiForAI] = useState(null);
  const [npiAiTitle, setNpiAiTitle] = useState("");
  const [individualAiData, setIndividualAiData] = useState(null);
  const [individualAiLoading, setIndividualAiLoading] = useState(false);
  const [individualModalOpen, setIndividualModalOpen] = useState(false);
  const [individualAiError, setIndividualAiError] = useState(null);

  const [portfolioAiData, setPortfolioAiData] = useState(null);
  const [portfolioAiLoading, setPortfolioAiLoading] = useState(false);
  const [portfolioModalOpen, setPortfolioModalOpen] = useState(false);
  const [portfolioAiError, setPortfolioAiError] = useState(null);
  const [exportLoading, setExportLoading] = useState(false);

  // AI Mitigation Plan states (Project Level)
  const [aiMitigationModalOpen, setAiMitigationModalOpen] = useState(false);
  const [aiMitigationLoading, setAiMitigationLoading] = useState(false);
  const [aiMitigationData, setAiMitigationData] = useState(null);
  const [selectedProjectForMitigation, setSelectedProjectForMitigation] = useState(null);
  const [aiMitigationError, setAiMitigationError] = useState(null);

  // AI Mitigation Plan states (Task / Issue Level)
  const [taskAiMitigationModalOpen, setTaskAiMitigationModalOpen] = useState(false);
  const [taskAiMitigationLoading, setTaskAiMitigationLoading] = useState(false);
  const [taskAiMitigationData, setTaskAiMitigationData] = useState(null);
  const [selectedTaskForMitigation, setSelectedTaskForMitigation] = useState(null);
  const [taskAiMitigationError, setTaskAiMitigationError] = useState(null);

  const issueTypes = [
    { id: 1, name: 'Bug' },
    { id: 2, name: 'Epic' }, 
    { id: 3, name: 'Story' },
    { id: 4, name: 'Task' }
  ];
  const statuses = [
    { id: 1, name: 'To Do' },
    { id: 2, name: 'In Progress' },
    { id: 3, name: 'Done' },
  ];
  const priorities = [
    { id: 1, name: 'Highest' },
    { id: 2, name: 'High' },
    { id: 3, name: 'Medium' },
    { id: 4, name: 'Low' },
    { id: 5, name: 'Lowest' }
  ];

  const handleOpenCardModal = (type) => {
    setCardModalType(type);
    if (type === "total") setModalTab(0);
    if (type === "npi") setSelectedNpiStatusFilter("All");
    setModalOpen(true);
  };

  const normalizeNpiStatus = (status) => {
    const s = String(status || "").toLowerCase().replace(/[\s_-]/g, "");
    if (s.includes("underreview")) return "Under Review";
    if (s.includes("feasibility")) return "Feasibility Check";
    if (s.includes("onhold")) return "On Hold";
    if (s.includes("approved")) return "Approved";
    if (s.includes("converted")) return "Converted";
    if (s.includes("submitted")) return "Submitted";
    if (s.includes("draft")) return "Draft";
    if (s.includes("rejected")) return "Rejected";
    return status || "Unknown";
  };

  const handleOpenNpiStatusModal = (statusName = "All") => {
    setSelectedNpiStatusFilter(statusName);
    setCardModalType("npi");
    setModalOpen(true);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      if (typeof dateStr === "string" && dateStr.includes("-") && !dateStr.includes("T")) {
        return dateStr;
      }
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return String(dateStr).split("T")[0];
      const day = String(d.getDate()).padStart(2, "0");
      const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
      const month = months[d.getMonth()];
      const year = d.getFullYear();
      return `${day}-${month}-${year}`;
    } catch (e) {
      return String(dateStr).split("T")[0];
    }
  };

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem("token") || localStorage.getItem("accessToken");
      const userStr = localStorage.getItem("user");
      let companyId = 1;
      if (userStr) {
        try {
          const userObj = JSON.parse(userStr);
          companyId = userObj.company_id || userObj.company_registration_no;
        } catch (e) {
          //console.error("Error parsing stored user:", e);
        }
      }

      const response = await axios.get(`${API_URL}/api/cpo-ppm-dashboard/summary`, {
        params: {
          company_id: companyId,
          projectId: selectedProject,
          programName: selectedProgram,
          monthDate: selectedMonth
        },
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        withCredentials: true
      });

      if (companyId) {
        axios.get(`${API_URL}/api/cmp/${companyId}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
          .then(res => {
            if (res.data?.data) setCompanyConfig(res.data.data);
          })
          .catch(() => {});
      }

      if (response.data?.success) {
        const rawData = response.data.data || {};
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const projectSummary = (rawData.projectSummary || []).map((p) => {
          const comp = Number(p.overallProgress !== undefined ? p.overallProgress : p.completionPercentage) || 0;
          const rawStatus = String(p.status || "").toLowerCase().trim();
          const isDone = comp >= 100 || rawStatus.includes("completed") || rawStatus.includes("closed") || rawStatus.includes("finish");

          if (isDone) return { ...p, status: "Completed", isCompleted: true };

          let calculatedStatus = "On Track";
          let isEndDatePassed = false;
          let daysRemaining = null;

          if (p.plannedEndDate && p.plannedEndDate !== "Not specified") {
            const endDateObj = new Date(p.plannedEndDate);
            if (!isNaN(endDateObj.getTime())) {
              endDateObj.setHours(23, 59, 59, 999);
              if (endDateObj < today) {
                isEndDatePassed = true;
              } else {
                daysRemaining = (endDateObj - today) / (1000 * 3600 * 24);
              }
            }
          }

          if (isEndDatePassed && comp < 100) {
            calculatedStatus = "Delayed";
          } else if (rawStatus.includes("delay") || rawStatus.includes("behind")) {
            calculatedStatus = "Delayed";
          } else if (daysRemaining !== null && daysRemaining <= 5 && comp < 95) {
            calculatedStatus = "At Risk";
          } else {
            calculatedStatus = "On Track";
          }

          return { ...p, status: calculatedStatus };
        });

        const activeProjects = projectSummary.filter(p => !p.isCompleted && p.status !== "Completed");
        const totalActive = activeProjects.length;
        const onTrackCount = activeProjects.filter(p => p.status === "On Track").length;
        const atRiskCount = activeProjects.filter(p => p.status === "At Risk").length;
        const delayedCount = activeProjects.filter(p => p.status === "Delayed").length;

        const updatedMetrics = {
          ...(rawData.metrics || {}),
          onTrack: { count: onTrackCount, percentage: totalActive > 0 ? Math.round((onTrackCount / totalActive) * 100) : 0 },
          atRisk: { count: atRiskCount, percentage: totalActive > 0 ? Math.round((atRiskCount / totalActive) * 100) : 0 },
          delayed: { count: delayedCount, percentage: totalActive > 0 ? Math.round((delayedCount / totalActive) * 100) : 0 }
        };

        setDashboardData({
          ...rawData,
          projectSummary,
          metrics: updatedMetrics
        });
      } else {
        throw new Error(response.data?.message || "Failed to load dashboard data");
      }
    } catch (err) {
      //console.error("CPO & PPM Dashboard error:", err);
      setError(err.message || "Failed to fetch dashboard metrics");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, [selectedProject, selectedProgram, selectedMonth]);

  const handleTriggerPortfolioAI = async () => {
    try {
      setPortfolioAiLoading(true);
      setPortfolioAiError(null);
      setPortfolioModalOpen(true);
      setPortfolioAiData(null);
      const token = localStorage.getItem("token") || localStorage.getItem("accessToken");
      const res = await axios.get(`${API_URL}/api/npi/portfolio/ai-analysis`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        withCredentials: true
      });
      if (res.data?.status === "success") {
        setPortfolioAiData(res.data.data);
      } else {
        throw new Error(res.data?.message || "Failed to fetch portfolio AI analysis.");
      }
    } catch (err) {
      //console.error("Portfolio AI Analysis Error:", err);
      const errorData = err.response?.data;
      if (errorData?.code === "AI_PLAN_UPGRADE_REQUIRED") {
        setPortfolioAiError({
          type: "upgrade",
          message: errorData.message || "AI features are not included in your current plan. Please upgrade to unlock AI.",
          severity: "warning"
        });
        window.dispatchEvent(new CustomEvent("openUpgradeModal", { detail: { feature: "AI NPI Strategic Analysis" } }));
      } else if (errorData?.code === "AI_TOKENS_EXHAUSTED") {
        setPortfolioAiError({
          type: "exhausted",
          message: `⚠️ ${errorData.message || "You have used all your AI tokens for this billing cycle."}`,
          severity: "warning"
        });
      } else {
        setPortfolioAiError({
          type: "error",
          message: errorData?.message || err.message || "Failed to fetch AI portfolio report.",
          severity: "error"
        });
      }
    } finally {
      setPortfolioAiLoading(false);
    }
  };

  const handleTriggerIndividualAI = async (npiId, title) => {
    try {
      setSelectedNpiForAI(npiId);
      setNpiAiTitle(title);
      setIndividualAiLoading(true);
      setIndividualAiError(null);
      setIndividualModalOpen(true);
      setIndividualAiData(null);
      const token = localStorage.getItem("token") || localStorage.getItem("accessToken");
      const res = await axios.get(`${API_URL}/api/npi/${npiId}/ai-recommendations`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        withCredentials: true
      });
      if (res.data?.status === "success") {
        setIndividualAiData(res.data.data);
      } else {
        throw new Error(res.data?.message || "Failed to fetch AI resource recommendations.");
      }
    } catch (err) {
      //console.error("Individual NPI AI Error:", err);
      const errorData = err.response?.data;
      if (errorData?.code === "AI_PLAN_UPGRADE_REQUIRED") {
        setIndividualAiError({
          type: "upgrade",
          message: errorData.message || "AI features are not included in your current plan. Please upgrade to unlock AI.",
          severity: "warning"
        });
        window.dispatchEvent(new CustomEvent("openUpgradeModal", { detail: { feature: "AI NPI Resource Matching" } }));
      } else if (errorData?.code === "AI_TOKENS_EXHAUSTED") {
        setIndividualAiError({
          type: "exhausted",
          message: `⚠️ ${errorData.message || "You have used all your AI tokens for this billing cycle."}`,
          severity: "warning"
        });
      } else {
        setIndividualAiError({
          type: "error",
          message: errorData?.message || err.message || "Failed to match resources.",
          severity: "error"
        });
      }
    } finally {
      setIndividualAiLoading(false);
    }
  };

  const handleExportPortfolioDocx = async () => {
    if (!portfolioAiData) return;
    try {
      setExportLoading(true);
      const token = localStorage.getItem("token") || localStorage.getItem("accessToken");
      const res = await axios.post(`${API_URL}/api/npi/portfolio/export-docx`, {
        title: "Executive Portfolio Strategic NPI AI Analysis",
        analysis: portfolioAiData
      }, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        responseType: "blob",
        withCredentials: true
      });
      
      const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document" });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = `NPI_Portfolio_AI_Report_${Date.now()}.docx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (err) {
      //console.error("Export Word error:", err);
      alert("Failed to export Word document");
    } finally {
      setExportLoading(false);
    }
  };

  const handleOpenProjectMitigation = async (project) => {
    try {
      setSelectedProjectForMitigation(project);
      setAiMitigationModalOpen(true);
      setAiMitigationLoading(true);
      setAiMitigationError(null);
      setAiMitigationData(null);

      const token = localStorage.getItem("token") || localStorage.getItem("accessToken");
      const projectId = project.id || project.project_id;

      const res = await axios.post(
        `${API_URL}/api/cpo-ppm-dashboard/project/${projectId}/ai-mitigation`,
        { customReason: project.reason || "" },
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          withCredentials: true
        }
      );

      if (res.data?.success) {
        setAiMitigationData(res.data.data);
      } else {
        throw new Error(res.data?.message || "Failed to generate AI mitigation plan.");
      }
    } catch (err) {
      //console.error("AI Mitigation Error:", err);
      const errorData = err.response?.data;
      if (errorData?.code === "AI_PLAN_UPGRADE_REQUIRED") {
        setAiMitigationError({
          type: "upgrade",
          message: errorData.message || "AI features are not included in your current plan. Please upgrade to unlock AI.",
          severity: "warning"
        });
        window.dispatchEvent(new CustomEvent("openUpgradeModal", { detail: { feature: "AI Project Risk Mitigation" } }));
      } else if (errorData?.code === "AI_TOKENS_EXHAUSTED") {
        setAiMitigationError({
          type: "exhausted",
          message: `⚠️ ${errorData.message || "You have used all your AI tokens for this billing cycle."}`,
          severity: "warning"
        });
      } else {
        setAiMitigationError({
          type: "error",
          message: errorData?.message || err.message || "Failed to generate AI recommendations.",
          severity: "error"
        });
      }
    } finally {
      setAiMitigationLoading(false);
    }
  };

  const handleOpenTaskMitigation = async (task) => {
    try {
      setSelectedTaskForMitigation(task);
      setTaskAiMitigationModalOpen(true);
      setTaskAiMitigationLoading(true);
      setTaskAiMitigationError(null);
      setTaskAiMitigationData(null);

      const token = localStorage.getItem("token") || localStorage.getItem("accessToken");
      const taskId = task.id || task.taskId;

      const res = await axios.post(
        `${API_URL}/api/cpo-ppm-dashboard/issue/${taskId}/ai-mitigation`,
        { customReason: task.reason || task.risk_reason || "" },
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          withCredentials: true
        }
      );

      if (res.data?.success) {
        setTaskAiMitigationData(res.data.data);
      } else {
        throw new Error(res.data?.message || "Failed to generate AI mitigation plan for task.");
      }
    } catch (err) {
      //console.error("Task AI Mitigation Error:", err);
      const errorData = err.response?.data;
      if (errorData?.code === "AI_PLAN_UPGRADE_REQUIRED") {
        setTaskAiMitigationError({
          type: "upgrade",
          message: errorData.message || "AI features are not included in your current plan. Please upgrade to unlock AI.",
          severity: "warning"
        });
        window.dispatchEvent(new CustomEvent("openUpgradeModal", { detail: { feature: "AI Task Risk Mitigation" } }));
      } else if (errorData?.code === "AI_TOKENS_EXHAUSTED") {
        setTaskAiMitigationError({
          type: "exhausted",
          message: `⚠️ ${errorData.message || "You have used all your AI tokens for this billing cycle."}`,
          severity: "warning"
        });
      } else {
        setTaskAiMitigationError({
          type: "error",
          message: errorData?.message || err.message || "Failed to generate AI recommendations.",
          severity: "error"
        });
      }
    } finally {
      setTaskAiMitigationLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="70vh">
        <CircularProgress size={50} thickness={4} style={{ color: "#2563eb" }} />
        <Typography variant="body1" sx={{ mt: 2, color: "#64748b", fontWeight: 500 }}>
          Loading Executive CPO & PPM Portfolio Dashboard...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={4}>
        <Alert severity="error" action={
          <Button color="inherit" size="small" onClick={fetchDashboardData} startIcon={<RefreshCw size={16} />}>
            Retry
          </Button>
        }>
          {error}
        </Alert>
      </Box>
    );
  }

  const {
    metrics = {},
    projectSummary = [],
    completedProjects = [],
    allProjects = [],
    issueOverview = { total: 48, open: { count: 22, percentage: 46 }, inProgress: { count: 14, percentage: 29 }, onHold: { count: 6, percentage: 13 }, closed: { count: 6, percentage: 12 } },
    npiOverview = { total: 0, submitted: 0, underReview: 0, approved: 0, onHold: 0, draft: 0, feasibility: 0, converted: 0, rejected: 0, npiList: [] },
    progressTrend = [],
    topOverdueTasks = [],
    recentIssues = [],
    recentNpiProposals = [],
    activeSprintIssues = [],
    filterOptions = { projects: [], programs: [] }
  } = dashboardData || {};
  const issuePieData = [
    { name: "Open", value: issueOverview.open?.count || 0, percentage: issueOverview.open?.percentage || 0, color: "#ef4444" },
    { name: "In Progress", value: issueOverview.inProgress?.count || 0, percentage: issueOverview.inProgress?.percentage || 0, color: "#3b82f6" },
    { name: "On Hold", value: issueOverview.onHold?.count || 0, percentage: issueOverview.onHold?.percentage || 0, color: "#f59e0b" },
    { name: "Closed", value: issueOverview.closed?.count || 0, percentage: issueOverview.closed?.percentage || 0, color: "#10b981" }
  ];

  const npiPieData = [
    { name: "Draft", value: npiOverview.draft || 0, color: "#94a3b8" },
    { name: "Submitted", value: npiOverview.submitted || 0, color: "#3b82f6" },
    { name: "Under Review", value: npiOverview.underReview || 0, color: "#f59e0b" },
    { name: "Feasibility Check", value: npiOverview.feasibility || 0, color: "#6366f1" },
    { name: "Approved", value: npiOverview.approved || 0, color: "#10b981" },
    { name: "Converted", value: npiOverview.converted || 0, color: "#8b5cf6" },
    { name: "On Hold", value: npiOverview.onHold || 0, color: "#f97316" },
    { name: "Rejected", value: npiOverview.rejected || 0, color: "#ef4444" }
  ];

  const activeNpiPieData = npiPieData.filter(item => item.value > 0);
  const displayNpiPieData = activeNpiPieData.length > 0 ? activeNpiPieData : [{ name: "No Proposals", value: 1, color: "#e2e8f0" }];

  const displayAtRiskCount = metrics.atRisk?.count !== undefined ? metrics.atRisk.count : 0;
  const displayAtRiskPct = metrics.atRisk?.percentage !== undefined ? metrics.atRisk.percentage : 0;

  const displayDelayedCount = metrics.delayed?.count !== undefined ? metrics.delayed.count : 0;
  const displayDelayedPct = metrics.delayed?.percentage !== undefined ? metrics.delayed.percentage : 0;

  const displayOnTrackCount = metrics.onTrack?.count !== undefined ? metrics.onTrack.count : 0;
  const displayOnTrackPct = metrics.onTrack?.percentage !== undefined ? metrics.onTrack.percentage : 0;

  const projectStatusPieData = [
    { name: "On Track", value: displayOnTrackCount, percentage: displayOnTrackPct, color: "#10b981" },
    { name: "At Risk", value: displayAtRiskCount, percentage: displayAtRiskPct, color: "#f59e0b" },
    { name: "Delayed", value: displayDelayedCount, percentage: displayDelayedPct, color: "#ef4444" }
  ];

  // Lists for Card Detail Modals (strictly from backend data)
  let onTrackProjectsList = projectSummary.filter(p => p.status === "On Track");
  let atRiskProjectsList = projectSummary.filter(p => p.status === "At Risk");
  let delayedProjectsList = projectSummary.filter(p => p.status === "Delayed");

  return (
    <Box sx={{ backgroundColor: "#f8fafc", minHeight: "100vh", p: { xs: 2, md: 3.5 } }}>
      
      {/* Top Header & Global Filters */}
      <Box display="flex" flexDirection={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} mb={3.5} gap={2}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700,  tracking: "-0.02em" }}>
            Dashboard
          </Typography>
        
        </Box>

        {/* Global Filter Bar */}
        <Box display="flex" flexWrap="wrap" gap={1.5} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 140, bg: "#ffffff" }}>
            <Select
              value={selectedProject}
              onChange={(e) => setSelectedProject(e.target.value)}
              displayEmpty
              sx={{ borderRadius: "8px", backgroundColor: "#ffffff", fontSize: "0.875rem" }}
            >
              <MenuItem value="all">All Projects</MenuItem>
              {filterOptions.projects?.map((p) => (
                <MenuItem key={p.id} value={p.id}>{p.key} - {p.name}</MenuItem>
              ))}
            </Select>
          </FormControl>

        

         

          <Tooltip title="Refresh Data">
            <IconButton onClick={fetchDashboardData} size="small" sx={{ backgroundColor: "#ffffff", borderRadius: "8px", border: "1px solid #e2e8f0", p: 1 }}>
              <RefreshCw size={18} color="#64748b" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Top 5 Executive Metric Cards - ALL CLICKABLE */}
      <Grid container spacing={2.5} mb={3.5}>
        
        {/* Card 1: Total Active Projects */}
        <Grid item xs={12} sm={6} md={2.4}>
          <Tooltip title="Click to view Active, Completed & Total Projects details">
            <Paper
              elevation={0}
              onClick={() => handleOpenCardModal("total")}
              sx={{
                p: 2.5,
                borderRadius: "12px",
                border: "1px solid #e2e8f0",
                backgroundColor: "#ffffff",
                height: "90%",
                cursor: "pointer",
                transition: "all 0.2s ease",
                "&:hover": {
                  boxShadow: "0 10px 20px -3px rgba(37, 99, 235, 0.15)",
                  borderColor: "#2563eb",
                  transform: "translateY(-2px)"
                }
              }}
            >
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  Total Projects
                </Typography>
                <Box sx={{ backgroundColor: "#eff6ff", p: 1, borderRadius: "50%" }}>
                  <FolderKanban size={20} color="#2563eb" />
                </Box>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 700, color: "#0f172a", mt: 1.5 }}>
                {metrics.totalActiveProjects !== undefined ? metrics.totalActiveProjects : (metrics.totalProjects || 0)}
              </Typography>
              <Typography variant="caption" sx={{ color: "#2563eb", fontWeight: 600, mt: 0.5, display: "block" }}>
                Active Projects
              </Typography>
            </Paper>
          </Tooltip>
        </Grid>

        {/* Card 2: On Track */}
        <Grid item xs={12} sm={6} md={2.4}>
          <Tooltip title="Click to view all On Track projects details">
            <Paper
              elevation={0}
              onClick={() => handleOpenCardModal("onTrack")}
              sx={{
                p: 2.5,
                borderRadius: "12px",
                border: "1px solid #e2e8f0",
                backgroundColor: "#ffffff",
                height: "90%",
                cursor: "pointer",
                transition: "all 0.2s ease",
                "&:hover": {
                  boxShadow: "0 10px 20px -3px rgba(22, 163, 74, 0.15)",
                  borderColor: "#16a34a",
                  transform: "translateY(-2px)"
                }
              }}
            >
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  On Track
                </Typography>
                <Box sx={{ backgroundColor: "#dcfce7", p: 1, borderRadius: "50%" }}>
                  <CheckCircle2 size={20} color="#16a34a" />
                </Box>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 700, color: "#16a34a", mt: 1.5 }}>
                {displayOnTrackCount}
              </Typography>
              <Typography variant="caption" sx={{ color: "#16a34a", fontWeight: 600, mt: 0.5, display: "block" }}>
                {displayOnTrackPct}% of Total 
              </Typography>
            </Paper>
          </Tooltip>
        </Grid>

        {/* Card 3: At Risk */}
        <Grid item xs={12} sm={6} md={2.4}>
          <Tooltip title="Click to view all At Risk projects details">
            <Paper
              elevation={0}
              onClick={() => handleOpenCardModal("atRisk")}
              sx={{
                p: 2.5,
                borderRadius: "12px",
                border: "1px solid #e2e8f0",
                backgroundColor: "#ffffff",
                height: "90%",
                cursor: "pointer",
                transition: "all 0.2s ease",
                "&:hover": {
                  boxShadow: "0 10px 20px -3px rgba(217, 119, 6, 0.15)",
                  borderColor: "#d97706",
                  transform: "translateY(-2px)"
                }
              }}
            >
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  At Risk
                </Typography>
                <Box sx={{ backgroundColor: "#fef3c7", p: 1, borderRadius: "50%" }}>
                  <AlertTriangle size={20} color="#d97706" />
                </Box>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 700, color: "#d97706", mt: 1.5 }}>
                {displayAtRiskCount}
              </Typography>
              <Typography variant="caption" sx={{ color: "#d97706", fontWeight: 600, mt: 0.5, display: "block" }}>
                {displayAtRiskPct}% of Total 
              </Typography>
            </Paper>
          </Tooltip>
        </Grid>

        {/* Card 4: Delayed */}
        <Grid item xs={12} sm={6} md={2.4}>
          <Tooltip title="Click to view all Delayed projects details">
            <Paper
              elevation={0}
              onClick={() => handleOpenCardModal("delayed")}
              sx={{
                p: 2.5,
                borderRadius: "12px",
                border: "1px solid #e2e8f0",
                backgroundColor: "#ffffff",
                height: "90%",
                cursor: "pointer",
                transition: "all 0.2s ease",
                "&:hover": {
                  boxShadow: "0 10px 20px -3px rgba(220, 38, 38, 0.15)",
                  borderColor: "#dc2626",
                  transform: "translateY(-2px)"
                }
              }}
            >
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  Delayed
                </Typography>
                <Box sx={{ backgroundColor: "#fee2e2", p: 1, borderRadius: "50%" }}>
                  <Calendar size={20} color="#dc2626" />
                </Box>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 700, color: "#dc2626", mt: 1.5 }}>
                {displayDelayedCount}
              </Typography>
              <Typography variant="caption" sx={{ color: "#dc2626", fontWeight: 600, mt: 0.5, display: "block" }}>
                {displayDelayedPct}% of Total 
              </Typography>
            </Paper>
          </Tooltip>
        </Grid>

        {/* Card 5: Overall Progress */}
        <Grid item xs={12} sm={6} md={2.4}>
          <Tooltip title="Click to view overall progress breakdown across projects">
            <Paper
              elevation={0}
              onClick={() => handleOpenCardModal("progress")}
              sx={{
                p: 2.5,
                borderRadius: "12px",
                border: "1px solid #e2e8f0",
                backgroundColor: "#ffffff",
                height: "90%",
                cursor: "pointer",
                display: "flex",
                justify: "space-between",
                alignItems: "center",
                transition: "all 0.2s ease",
                "&:hover": {
                  boxShadow: "0 10px 20px -3px rgba(37, 99, 235, 0.15)",
                  borderColor: "#2563eb",
                  transform: "translateY(-2px)"
                }
              }}
            >
              <Box>
                <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  Overall Progress
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: "#0f172a", mt: 1.5 }}>
                  {metrics.overallProgress || 0}%
                </Typography>
                <Typography variant="caption" sx={{ color: "#2563eb", fontWeight: 600, mt: 0.5, display: "block" }}>
                  Average 
                </Typography>
              </Box>

              {/* SVG Circular Progress Ring */}
              <Box position="relative" display="inline-flex">
                <svg width="60" height="60">
                  <circle cx="30" cy="30" r="24" stroke="#e2e8f0" strokeWidth="5" fill="transparent" />
                  <circle
                    cx="30"
                    cy="30"
                    r="24"
                    stroke="#2563eb"
                    strokeWidth="5"
                    fill="transparent"
                    strokeDasharray={150}
                    strokeDashoffset={150 - (150 * (metrics.overallProgress || 0)) / 100}
                    strokeLinecap="round"
                  />
                </svg>
                <Box
                  position="absolute"
                  top={0}
                  left={0}
                  bottom={0}
                  right={0}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <Typography variant="caption" sx={{ fontWeight: 700, fontSize: "0.75rem", color: "#1e293b" }}>
                    {metrics.overallProgress || 0}%
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Tooltip>
        </Grid>

      </Grid>

      {/* Row 2: Project Summary Table (8 Cols) & Issues Overview Donut Chart (4 Cols) */}
      <Grid container spacing={2.5} mb={3.5}>
        
        {/* Project Summary Table */}
        <Grid item xs={12} lg={8}>
          <Paper elevation={0} sx={{ borderRadius: "12px", border: "1px solid #e2e8f0", backgroundColor: "#ffffff", p: 2.5, height: "100%", display: "flex", flexDirection: "column" }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: "#0f172a", fontSize: "1rem" }}>
                Project Summary (Active Projects)
              </Typography>
            </Box>

            <TableContainer sx={{ flexGrow: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#f8fafc" }}>
                    <TableCell sx={{ fontWeight: 600, color: "#64748b", py: 1.5 }}>Project Code</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: "#64748b" }}>Project Name</TableCell>
             
                    <TableCell sx={{ fontWeight: 600, color: "#64748b" }}>Overall Progress</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: "#64748b" }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: "#64748b" }}>Planned End Date</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, color: "#64748b" }}>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {projectSummary
                    .filter((row) => Number(row.overallProgress) < 100 && row.status !== "Completed")
                    .slice(projectPage * projectRowsPerPage, projectPage * projectRowsPerPage + projectRowsPerPage)
                    .map((row) => (
                    <TableRow key={row.id} hover sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                      <TableCell sx={{ fontWeight: 600, color: "#334155", fontSize: "0.85rem" }}>
                        {row.key}
                      </TableCell>
                      <TableCell sx={{ color: "#0f172a", fontWeight: 500, fontSize: "0.875rem" }}>
                        {row.name}
                      </TableCell>
                  
                      <TableCell sx={{ width: "20%" }}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <LinearProgress
                            variant="determinate"
                            value={row.overallProgress}
                            sx={{
                              flexGrow: 1,
                              height: 6,
                              borderRadius: 3,
                              backgroundColor: "#e2e8f0",
                              "& .MuiLinearProgress-bar": {
                                backgroundColor: row.status === "On Track" ? "#10b981" : row.status === "At Risk" ? "#f59e0b" : "#ef4444"
                              }
                            }}
                          />
                          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: "0.8rem", minWidth: 32 }}>
                            {row.overallProgress}%
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={row.status}
                          size="small"
                          sx={{
                            fontWeight: 600,
                            fontSize: "0.75rem",
                            backgroundColor: row.status === "On Track" ? "#dcfce7" : row.status === "At Risk" ? "#fef3c7" : "#fee2e2",
                            color: row.status === "On Track" ? "#15803d" : row.status === "At Risk" ? "#b45309" : "#b91c1c",
                            borderRadius: "6px"
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ color: "#64748b", fontSize: "0.85rem" }}>
                        {formatDate(row.plannedEndDate)}
                      </TableCell>
                      <TableCell align="right">
                        {row.status === "At Risk" || row.status === "Delayed" ? (
                          <Button
                            size="small"
                            variant="contained"
                            startIcon={<Sparkles size={13} />}
                            onClick={() => handleOpenProjectMitigation(row)}
                            sx={{
                              borderRadius: 1.5,
                              textTransform: "none",
                              fontWeight: 600,
                              fontSize: "0.75rem",
                              backgroundColor: row.status === "At Risk" ? "#d97706" : "#dc2626",
                              "&:hover": {
                                backgroundColor: row.status === "At Risk" ? "#b45309" : "#b91c1c"
                              }
                            }}
                          >
                            View
                          </Button>
                        ) : (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => navigate(`/project/${row.id || row.project_id}/db`)}
                            sx={{ borderRadius: 1.5, textTransform: "none", fontWeight: 600, fontSize: "0.75rem" }}
                          >
                            View
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {projectSummary.filter((row) => Number(row.overallProgress) < 100 && row.status !== "Completed").length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 3, color: "#64748b" }}>
                        No Active Projects Found. Click "Total Projects" card to view completed projects.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Box display="flex" justifyContent="space-between" alignItems="center" pt={1.5} flexWrap="wrap" gap={1}>
              <Button size="small" onClick={() => handleOpenCardModal("total")} endIcon={<ArrowUpRight size={16} />} sx={{ textTransform: "none", color: "#2563eb", fontWeight: 600 }}>
                View all projects
              </Button>
              <TablePagination
                component="div"
                count={projectSummary.filter((row) => Number(row.overallProgress) < 100 && row.status !== "Completed").length}
                page={projectPage}
                onPageChange={handleChangeProjectPage}
                rowsPerPage={projectRowsPerPage}
                onRowsPerPageChange={handleChangeProjectRowsPerPage}
                rowsPerPageOptions={[4, 5, 10]}
                sx={{
                  border: "none",
                  ".MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows": {
                    fontSize: "0.8rem",
                    color: "#64748b",
                    m: 0
                  },
                  ".MuiTablePagination-toolbar": {
                    minHeight: 36,
                    px: 0
                  }
                }}
              />
            </Box>
          </Paper>
        </Grid>

        {/* NPI Proposals Breakdown Donut Chart */}
        <Grid item xs={12} lg={4}>
          <Paper
            elevation={0}
            onClick={() => handleOpenCardModal("npi")}
            sx={{
              borderRadius: "12px",
              border: "1px solid #e2e8f0",
              backgroundColor: "#ffffff",
              p: 2.5,
              height: "100%",
              display: "flex",
              flexDirection: "column",
              cursor: "pointer",
              transition: "all 0.2s ease",
              "&:hover": {
                boxShadow: "0 10px 20px -3px rgba(37, 99, 235, 0.12)",
                borderColor: "#2563eb",
                transform: "translateY(-2px)"
              }
            }}
          >
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: "#0f172a", fontSize: "1rem" }}>
                  NPI Proposals Breakdown
                </Typography>
                <Typography variant="caption" sx={{ color: "#2563eb", fontWeight: 600, display: "block", mt: 0.2 }}>
                  New Product Initiative
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={1}>
                <IconButton
                  size="small"
                  color="primary"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleTriggerPortfolioAI();
                  }}
                  sx={{ bgcolor: "#eff6ff", "&:hover": { bgcolor: "#dbeafe" }, p: 0.5 }}
                >
                  <Sparkles size={14} style={{ color: "#2563eb" }} />
                </IconButton>
                <Chip
                  label="Portfolio Intake"
                  size="small"
                  sx={{ backgroundColor: "#eff6ff", color: "#2563eb", fontWeight: 700, fontSize: "0.7rem", borderRadius: "6px" }}
                />
              </Box>
            </Box>

            <Box position="relative" height={220} width="100%" display="flex" alignItems="center" justifyContent="center">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={displayNpiPieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={85}
                    paddingAngle={3}
                    dataKey="value"
                    style={{ cursor: "pointer" }}
                  >
                    {displayNpiPieData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={entry.color}
                        onClick={(e) => {
                          e?.stopPropagation?.();
                          if (entry.name && entry.name !== "No Proposals") {
                            handleOpenNpiStatusModal(entry.name);
                          }
                        }}
                      />
                    ))}
                  </Pie>
                  <RechartsTooltip />
                </PieChart>
              </ResponsiveContainer>

              <Box position="absolute" textAlign="center" pointerEvents="none">
                <Typography variant="h5" sx={{ fontWeight: 800, color: "#0f172a" }}>
                  {npiOverview.total || 0}
                </Typography>
                <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600, display: "block" }}>
                  Total Proposals
                </Typography>
              </Box>
            </Box>

            <Box mt={2}>
              <Grid container spacing={1}>
                {npiPieData.filter(item => item.value > 0).map((item, idx) => (
                  <Grid item xs={6} key={idx}>
                    <Box
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenNpiStatusModal(item.name);
                      }}
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      p={0.75}
                      borderRadius="6px"
                      sx={{
                        backgroundColor: "#f8fafc",
                        cursor: "pointer",
                        transition: "all 0.15s ease",
                        "&:hover": {
                          backgroundColor: "#eff6ff",
                          transform: "translateY(-1px)",
                          boxShadow: "0 2px 5px rgba(0,0,0,0.05)"
                        }
                      }}
                    >
                      <Box display="flex" alignItems="center" gap={1}>
                        <Box sx={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: item.color }} />
                        <Typography variant="caption" sx={{ fontWeight: 600, color: "#334155" }}>
                          {item.name}
                        </Typography>
                      </Box>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: "#0f172a" }}>
                        {item.value}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
                {activeNpiPieData.length === 0 && (
                  <Grid item xs={12}>
                    <Typography variant="caption" align="center" display="block" color="text.secondary" sx={{ py: 1 }}>
                      No active proposals found.
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Box>
          </Paper>
        </Grid>

      </Grid>

      {/* Row 3: Projects by Status (4 Cols), Progress Trend (4 Cols), Top 5 Overdue Tasks (4 Cols) */}
      <Grid container spacing={2.5} mb={3.5}>
        
        {/* Projects by Status */}
        <Grid item xs={12} md={4}>
          <Paper elevation={0} sx={{ borderRadius: "12px", border: "1px solid #e2e8f0", backgroundColor: "#ffffff", p: 2.5, height: "100%", display: "flex", flexDirection: "column" }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: "#0f172a", fontSize: "1rem", mb: 1 }}>
              Projects by Status
            </Typography>

            <Box position="relative" height={200} width="100%" display="flex" alignItems="center" justifyContent="center">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={projectStatusPieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={55}
                    outerRadius={80}
                    paddingAngle={4}
                    dataKey="value"
                    style={{ cursor: "pointer" }}
                  >
                    {projectStatusPieData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={entry.color}
                        onClick={() => {
                          const typeMap = {
                            "On Track": "onTrack",
                            "At Risk": "atRisk",
                            "Delayed": "delayed"
                          };
                          handleOpenCardModal(typeMap[entry.name] || "total");
                        }}
                      />
                    ))}
                  </Pie>
                  <RechartsTooltip />
                </PieChart>
              </ResponsiveContainer>

              <Box position="absolute" textAlign="center" pointerEvents="none">
                <Typography variant="h5" sx={{ fontWeight: 800, color: "#0f172a" }}>
                  {metrics.totalActiveProjects || 0}
                </Typography>
                <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600 }}>
                  Active
                </Typography>
              </Box>
            </Box>

            <Box mt={1.5} display="flex" flexDirection="column" gap={1}>
              {projectStatusPieData.map((item, idx) => {
                const typeMap = {
                  "On Track": "onTrack",
                  "At Risk": "atRisk",
                  "Delayed": "delayed"
                };
                return (
                  <Box
                    key={idx}
                    onClick={() => handleOpenCardModal(typeMap[item.name] || "total")}
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    px={1}
                    py={0.5}
                    sx={{
                      cursor: "pointer",
                      borderRadius: "6px",
                      transition: "all 0.15s ease",
                      "&:hover": { backgroundColor: "#f1f5f9" }
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={1}>
                      <Box sx={{ width: 10, height: 10, borderRadius: "2px", backgroundColor: item.color }} />
                      <Typography variant="body2" sx={{ fontWeight: 500, color: "#334155" }}>
                        {item.name}
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: "#0f172a" }}>
                      {item.value} ({item.percentage}%)
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Paper>
        </Grid>

        {/* Progress Trend (All Projects) */}
        <Grid item xs={12} md={4}>
          <Paper elevation={0} sx={{ borderRadius: "12px", border: "1px solid #e2e8f0", backgroundColor: "#ffffff", p: 2.5, height: "100%", display: "flex", flexDirection: "column" }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: "#0f172a", fontSize: "1rem" }}>
                Progress Trend (All Projects)
              </Typography>
              <Chip
                label="Monthly Portfolio"
                size="small"
                sx={{ height: 20, fontSize: "0.7rem", fontWeight: 700, bgcolor: "#eff6ff", color: "#2563eb" }}
              />
            </Box>

            <Box height={240} width="100%">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={progressTrend} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="month" stroke="#94a3b8" fontSize={12} />
                  <YAxis domain={[0, 100]} stroke="#94a3b8" fontSize={12} tickFormatter={(val) => `${val}%`} />
                  <RechartsTooltip
                    wrapperStyle={{ pointerEvents: "none", zIndex: 1200 }}
                    position={{ y: 5 }}
                    content={({ active, payload, label }) => {
                      if (active && payload && payload.length) {
                        const itemData = payload[0].payload;
                        const projectList = itemData.projects || [];
                        const displayList = projectList.slice(0, 3);
                        const remainingCount = projectList.length - displayList.length;

                        return (
                          <Paper
                            elevation={3}
                            sx={{
                              p: 1.2,
                              borderRadius: "8px",
                              border: "1px solid #e2e8f0",
                              backgroundColor: "rgba(255, 255, 255, 0.95)",
                              backdropFilter: "blur(4px)",
                              boxShadow: "0 6px 16px rgba(0, 0, 0, 0.08)",
                              width: 200,
                              pointerEvents: "none"
                            }}
                          >
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.6} pb={0.4} borderBottom="1px solid #f1f5f9">
                              <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#0f172a", fontSize: "0.78rem" }}>
                                {label}
                              </Typography>
                              <Chip
                                label={`${itemData.progress}%`}
                                size="small"
                                sx={{ height: 18, fontSize: "0.68rem", fontWeight: 800, bgcolor: "#eff6ff", color: "#2563eb" }}
                              />
                            </Box>

                            <Box display="flex" flexDirection="column" gap={0.4}>
                              {displayList.map((proj, idx) => (
                                <Box key={idx} display="flex" justifyContent="space-between" alignItems="center" gap={0.5}>
                                  <Box display="flex" alignItems="center" gap={0.5} sx={{ minWidth: 0, flex: 1 }}>
                                    <Box
                                      sx={{
                                        width: 5,
                                        height: 5,
                                        borderRadius: "50%",
                                        bgcolor:
                                          String(proj.status).toLowerCase().includes("risk") ? "#f59e0b" :
                                          String(proj.status).toLowerCase().includes("delay") ? "#ef4444" : "#10b981",
                                        flexShrink: 0
                                      }}
                                    />
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        fontWeight: 600,
                                        color: "#334155",
                                        fontSize: "0.7rem",
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis"
                                      }}
                                    >
                                      {proj.name}
                                    </Typography>
                                  </Box>
                                  <Typography variant="caption" sx={{ fontWeight: 700, color: "#64748b", fontSize: "0.68rem", flexShrink: 0 }}>
                                    {proj.progress}%
                                  </Typography>
                                </Box>
                              ))}

                              {remainingCount > 0 && (
                                <Typography variant="caption" sx={{ color: "#94a3b8", fontSize: "0.65rem", fontWeight: 600, textAlign: "center", pt: 0.2 }}>
                                  +{remainingCount} more projects
                                </Typography>
                              )}
                            </Box>
                          </Paper>
                        );
                      }
                      return null;
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="progress"
                    stroke="#2563eb"
                    strokeWidth={3}
                    dot={{ r: 4, fill: "#2563eb", strokeWidth: 2, stroke: "#ffffff" }}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>

        {/* Top 5 Overdue Tasks */}
        <Grid item xs={12} md={4}>
          <Paper elevation={0} sx={{ borderRadius: "12px", border: "1px solid #e2e8f0", backgroundColor: "#ffffff", p: 2.5, height: "100%", display: "flex", flexDirection: "column" }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: "#0f172a", fontSize: "1rem", mb: 1.5 }}>
              Top 5 Overdue Tasks
            </Typography>

            <TableContainer sx={{ flexGrow: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#f8fafc" }}>
                    <TableCell sx={{ fontWeight: 600, color: "#64748b", py: 1 }}>Task ID</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: "#64748b" }}>Task Name</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: "#64748b" }}>Project</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: "#64748b" }}>Due Date</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, color: "#64748b" }}>Overdue Days</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {topOverdueTasks.map((task, idx) => (
                    <TableRow
                      key={idx}
                      hover
                      onClick={() => handleOpenTaskMitigation(task)}
                      sx={{
                        "&:last-child td, &:last-child th": { border: 0 },
                        cursor: "pointer"
                      }}
                    >
                      <TableCell sx={{ fontWeight: 600, color: "#334155", fontSize: "0.8rem" }}>
                        {task.taskId}
                      </TableCell>
                      <TableCell sx={{ color: "#0f172a", fontWeight: 500, fontSize: "0.8rem", maxWidth: 120, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {task.taskName}
                      </TableCell>
                      <TableCell sx={{ color: "#64748b", fontSize: "0.8rem" }}>
                        {task.project}
                      </TableCell>
                      <TableCell sx={{ color: "#64748b", fontSize: "0.8rem" }}>
                        {formatDate(task.dueDate)}
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="caption" sx={{ fontWeight: 700, color: task.daysOverdue > 0 ? "#dc2626" : "#64748b" }}>
                          {task.daysOverdue}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                  {topOverdueTasks.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 2, color: "#64748b" }}>
                        No Overdue Tasks
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

      </Grid>

      {/* Row 4: Recent NPI Proposals Table */}
      <Paper elevation={0} sx={{ borderRadius: "12px", border: "1px solid #e2e8f0", backgroundColor: "#ffffff", p: 2.5 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: "#0f172a", fontSize: "1rem", mb: 2 }}>
          Recent NPI Proposals
        </Typography>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: "#f8fafc" }}>
                <TableCell sx={{ fontWeight: 600, color: "#64748b", py: 1.5 }}>Proposal Title</TableCell>
                <TableCell sx={{ fontWeight: 600, color: "#64748b" }}>Category</TableCell>
                <TableCell sx={{ fontWeight: 600, color: "#64748b" }}>Priority</TableCell>
                <TableCell sx={{ fontWeight: 600, color: "#64748b" }}>Strategic Alignment</TableCell>
                <TableCell sx={{ fontWeight: 600, color: "#64748b" }}>Risk Level</TableCell>
                <TableCell sx={{ fontWeight: 600, color: "#64748b" }}>Estimated Budget</TableCell>
                <TableCell sx={{ fontWeight: 600, color: "#64748b" }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600, color: "#64748b" }}>Submitted By</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600, color: "#64748b" }}>AI Recommendation</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600, color: "#64748b" }}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {recentNpiProposals.map((npi, idx) => (
                <TableRow
                  key={idx}
                  hover
                  onClick={() => {
                    navigate(`/npi-intake?openId=${npi.id}`, { state: { openIntakeId: npi.id } });
                  }}
                  sx={{
                    "&:last-child td, &:last-child th": { border: 0 },
                    cursor: "pointer"
                  }}
                >
                  <TableCell sx={{ color: "#0f172a", fontWeight: 600, fontSize: "0.875rem" }}>
                    {npi.title}
                  </TableCell>
                  <TableCell sx={{ color: "#64748b", fontSize: "0.85rem" }}>
                    {npi.category}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={npi.priority}
                      size="small"
                      sx={{
                        fontWeight: 600,
                        fontSize: "0.75rem",
                        backgroundColor: npi.priority === "Critical" || npi.priority === "High" ? "#fee2e2" : npi.priority === "Medium" ? "#fef3c7" : "#e0f2fe",
                        color: npi.priority === "Critical" || npi.priority === "High" ? "#b91c1c" : npi.priority === "Medium" ? "#b45309" : "#0369a1",
                        borderRadius: "6px"
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "#334155" }}>
                    {npi.alignment}/10
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={npi.risk}
                      size="small"
                      sx={{
                        fontWeight: 600,
                        fontSize: "0.75rem",
                        backgroundColor: npi.risk === "High" ? "#fee2e2" : npi.risk === "Medium" ? "#fef3c7" : "#dcfce7",
                        color: npi.risk === "High" ? "#b91c1c" : npi.risk === "Medium" ? "#b45309" : "#15803d",
                        borderRadius: "6px"
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "#0f172a" }}>
                    {companyConfig?.currency_symbol || '₹'}{Number(npi.budget).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </TableCell>
            
                  <TableCell>
                    <Box>
                      <Chip
                        label={npi.status}
                        size="small"
                        sx={{
                          fontWeight: 600,
                          fontSize: "0.75rem",
                          backgroundColor: npi.status === "Approved" || npi.status === "Converted" ? "#dcfce7" : npi.status === "Rejected" ? "#fee2e2" : "#f1f5f9",
                          color: npi.status === "Approved" || npi.status === "Converted" ? "#15803d" : npi.status === "Rejected" ? "#b91c1c" : "#475569",
                          borderRadius: "6px"
                        }}
                      />
                      {npi.status_change_reason && (
                        <Tooltip title={`Status Justification: ${npi.status_change_reason}`} arrow placement="top">
                          <Typography
                            variant="caption"
                            color="#64748B"
                            sx={{
                              display: "-webkit-box",
                              WebkitLineClamp: 1,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              mt: 0.4,
                              fontSize: "0.7rem",
                              fontStyle: "italic",
                              maxWidth: 130
                            }}
                          >
                            "{npi.status_change_reason}"
                          </Typography>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ color: "#334155", fontWeight: 600, fontSize: "0.85rem", whiteSpace: "nowrap" }}>
                    {npi.submittedBy || "Team Member"}
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="AI Recommendation">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleTriggerIndividualAI(npi.id, npi.title);
                        }}
                        sx={{ bgcolor: "#eff6ff", "&:hover": { bgcolor: "#dbeafe" } }}
                      >
                        <Sparkles size={14} style={{ color: "#2563eb" }} />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center">
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/npi-intake?openId=${npi.id}`, { state: { openIntakeId: npi.id } });
                      }}
                      sx={{ borderRadius: 1.5, textTransform: "none", fontWeight: 600, fontSize: "0.75rem" }}
                    >
                      View
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {recentNpiProposals.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 2, color: "#64748b" }}>
                    No Recent NPI Proposals
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Universal Interactive Cards Detail Dialog */}
      <Dialog
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { borderRadius: "16px", p: 1 }
        }}
      >
        <DialogTitle display="flex" justifyContent="space-between" alignItems="center" pb={1}>
          <Box display="flex" alignItems="center" gap={1.5}>
            <Box
              sx={{
                p: 1,
                borderRadius: "8px",
                backgroundColor: cardModalType === "onTrack" ? "#dcfce7" : cardModalType === "atRisk" ? "#fef3c7" : cardModalType === "delayed" ? "#fee2e2" : "#eff6ff"
              }}
            >
              {cardModalType === "onTrack" && <CheckCircle2 size={24} color="#16a34a" />}
              {cardModalType === "atRisk" && <AlertTriangle size={24} color="#d97706" />}
              {cardModalType === "delayed" && <Calendar size={24} color="#dc2626" />}
              {(cardModalType === "total" || cardModalType === "progress") && <FolderKanban size={24} color="#2563eb" />}
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: "#0f172a" }}>
                {cardModalType === "total" && "Company Portfolio Projects Overview"}
                {cardModalType === "onTrack" && "On Track Projects Details"}
                {cardModalType === "atRisk" && "At Risk Projects Details"}
                {cardModalType === "delayed" && "Delayed Projects Details"}
                {cardModalType === "progress" && "Overall Portfolio Progress Breakdown"}
                {cardModalType === "issues" && "Active Sprint Issues List"}
                {cardModalType === "npi" && "NPI Proposals Intake List"}
              </Typography>
              <Typography variant="body2" sx={{ color: "#64748b" }}>
                {cardModalType === "total" && "Detailed View of Active, Completed & Total Projects"}
                {cardModalType === "onTrack" && "All projects currently running smoothly on schedule"}
                {cardModalType === "atRisk" && "All projects requiring proactive management attention"}
                {cardModalType === "delayed" && "All projects behind target completion dates"}
                {cardModalType === "progress" && "Full progress breakdown across active projects"}
                {cardModalType === "issues" && "All active sprint issues associated with active projects"}
                {cardModalType === "npi" && "Detailed View of New Product Initiative Proposals"}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={() => setModalOpen(false)} size="small">
            <X size={20} color="#64748b" />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ borderBottom: "none", pt: 2 }}>
          
          {/* Card-Specific Banner Summary */}
          <Grid container spacing={2} mb={3}>
            {cardModalType === "total" && (
              <>
                <Grid item xs={12} sm={4}>
                  <Paper elevation={0} sx={{ p: 2, borderRadius: "10px", backgroundColor: "#eff6ff", border: "1px solid #bfdbfe" }}>
                    <Typography variant="caption" sx={{ color: "#1e40af", fontWeight: 600, textTransform: "uppercase" }}>Active Projects</Typography>
                    <Typography variant="h4" sx={{ fontWeight: 800, color: "#1d4ed8", mt: 0.5 }}>{metrics.totalActiveProjects !== undefined ? metrics.totalActiveProjects : projectSummary.length}</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Paper elevation={0} sx={{ p: 2, borderRadius: "10px", backgroundColor: "#f0fdf4", border: "1px solid #bbf7d0" }}>
                    <Typography variant="caption" sx={{ color: "#166534", fontWeight: 600, textTransform: "uppercase" }}>Completed Projects</Typography>
                    <Typography variant="h4" sx={{ fontWeight: 800, color: "#15803d", mt: 0.5 }}>{metrics.totalCompletedProjects !== undefined ? metrics.totalCompletedProjects : completedProjects.length}</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Paper elevation={0} sx={{ p: 2, borderRadius: "10px", backgroundColor: "#f8fafc", border: "1px solid #e2e8f0" }}>
                    <Typography variant="caption" sx={{ color: "#475569", fontWeight: 600, textTransform: "uppercase" }}>Total Portfolio</Typography>
                    <Typography variant="h4" sx={{ fontWeight: 800, color: "#0f172a", mt: 0.5 }}>{metrics.totalAllProjects !== undefined ? metrics.totalAllProjects : (projectSummary.length + completedProjects.length)}</Typography>
                  </Paper>
                </Grid>
              </>
            )}

            {cardModalType === "onTrack" && (
              <Grid item xs={12}>
                <Paper elevation={0} sx={{ p: 2, borderRadius: "10px", backgroundColor: "#f0fdf4", border: "1px solid #bbf7d0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 700, color: "#15803d" }}>
                      {onTrackProjectsList.length} Projects On Track ({displayOnTrackPct}% of Active Portfolio)
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#166534" }}>These projects are progressing according to milestone schedules without critical blockers.</Typography>
                  </Box>
                  <Chip label="Healthy Status" sx={{ backgroundColor: "#15803d", color: "#ffffff", fontWeight: 700 }} />
                </Paper>
              </Grid>
            )}

            {cardModalType === "atRisk" && (
              <Grid item xs={12}>
                <Paper elevation={0} sx={{ p: 2, borderRadius: "10px", backgroundColor: "#fffbeb", border: "1px solid #fde68a", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 700, color: "#b45309" }}>
                      {atRiskProjectsList.length} Projects At Risk ({displayAtRiskPct}% of Active Portfolio)
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#92400e" }}>These projects require executive intervention or resource reallocation to prevent schedule slippage.</Typography>
                  </Box>
                  <Chip label="Attention Needed" sx={{ backgroundColor: "#d97706", color: "#ffffff", fontWeight: 700 }} />
                </Paper>
              </Grid>
            )}

            {cardModalType === "delayed" && (
              <Grid item xs={12}>
                <Paper elevation={0} sx={{ p: 2, borderRadius: "10px", backgroundColor: "#fef2f2", border: "1px solid #fecaca", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 700, color: "#b91c1c" }}>
                      {delayedProjectsList.length} Delayed Projects ({displayDelayedPct}% of Active Portfolio)
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#991b1b" }}>These projects have exceeded planned milestone dates and are behind schedule.</Typography>
                  </Box>
                  <Chip label="Critical Delay" sx={{ backgroundColor: "#dc2626", color: "#ffffff", fontWeight: 700 }} />
                </Paper>
              </Grid>
            )}

            {cardModalType === "progress" && (
              <Grid item xs={12}>
                <Paper elevation={0} sx={{ p: 2, borderRadius: "10px", backgroundColor: "#eff6ff", border: "1px solid #bfdbfe", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 700, color: "#1d4ed8" }}>
                      Overall Average Completion Rate: {metrics.overallProgress || 0}%
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#1e40af" }}>Calculated from real-time work item completion rates across all active portfolio projects.</Typography>
                  </Box>
                  <Chip label={`${metrics.overallProgress || 0}% Average`} sx={{ backgroundColor: "#2563eb", color: "#ffffff", fontWeight: 700 }} />
                </Paper>
              </Grid>
            )}

            {cardModalType === "issues" && (
              <Grid item xs={12}>
                <Paper elevation={0} sx={{ p: 2, borderRadius: "10px", backgroundColor: "#eff6ff", border: "1px solid #bfdbfe", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 700, color: "#1d4ed8" }}>
                      Active Sprint Work Items: {activeSprintIssues.length} Issues
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#1e40af" }}>
                      Breakdown: {issueOverview.open?.count || 0} Open, {issueOverview.inProgress?.count || 0} In Progress, {issueOverview.onHold?.count || 0} On Hold, {issueOverview.closed?.count || 0} Closed
                    </Typography>
                  </Box>
                  <Chip label="Sprint Backlog" sx={{ backgroundColor: "#2563eb", color: "#ffffff", fontWeight: 700 }} />
                </Paper>
              </Grid>
            )}

            {cardModalType === "npi" && (
              <Grid item xs={12}>
                <Paper elevation={0} sx={{ p: 2, borderRadius: "10px", backgroundColor: "#eff6ff", border: "1px solid #bfdbfe", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 700, color: "#1d4ed8" }}>
                      New Product Initiative (NPI): {selectedNpiStatusFilter === "All" ? `${npiOverview.total || 0} Total Proposals` : `${selectedNpiStatusFilter} (${(npiOverview.npiList || []).filter(n => normalizeNpiStatus(n.status) === selectedNpiStatusFilter).length} Proposals)`}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#1e40af" }}>
                      Click any status filter chip below or donut slice to inspect proposals & their status justification reasons.
                    </Typography>
                  </Box>
                  <Chip
                    label={selectedNpiStatusFilter === "All" ? "All Proposals" : `Status: ${selectedNpiStatusFilter}`}
                    sx={{ backgroundColor: "#2563eb", color: "#ffffff", fontWeight: 700 }}
                  />
                </Paper>
              </Grid>
            )}
          </Grid>

          {/* Status Filter Chips for NPI Modal */}
          {cardModalType === "npi" && (
            <Box sx={{ mb: 2 }}>
              <Box display="flex" flexWrap="wrap" gap={0.75} alignItems="center">
                <Typography variant="caption" sx={{ fontWeight: 700, color: "#64748b", textTransform: "uppercase", mr: 0.5 }}>
                  Filter Status:
                </Typography>
                {[
                  { name: "All", count: npiOverview.total || 0, color: "#2563eb" },
                  { name: "Draft", count: npiOverview.draft || 0, color: "#94a3b8" },
                  { name: "Submitted", count: npiOverview.submitted || 0, color: "#3b82f6" },
                  { name: "Under Review", count: npiOverview.underReview || 0, color: "#f59e0b" },
                  { name: "Feasibility Check", count: npiOverview.feasibility || 0, color: "#6366f1" },
                  { name: "Approved", count: npiOverview.approved || 0, color: "#10b981" },
                  { name: "Converted", count: npiOverview.converted || 0, color: "#8b5cf6" },
                  { name: "On Hold", count: npiOverview.onHold || 0, color: "#f97316" },
                  { name: "Rejected", count: npiOverview.rejected || 0, color: "#ef4444" }
                ].map((st) => {
                  const isSelected = selectedNpiStatusFilter === st.name;
                  if (st.name !== "All" && st.count === 0) return null;
                  return (
                    <Chip
                      key={st.name}
                      label={`${st.name} (${st.count})`}
                      size="small"
                      onClick={() => setSelectedNpiStatusFilter(st.name)}
                      sx={{
                        cursor: "pointer",
                        fontWeight: isSelected ? 700 : 500,
                        fontSize: "0.75rem",
                        bgcolor: isSelected ? st.color : "#f1f5f9",
                        color: isSelected ? "#ffffff" : "#334155",
                        border: isSelected ? `1px solid ${st.color}` : "1px solid #e2e8f0",
                        "&:hover": {
                          bgcolor: isSelected ? st.color : "#e2e8f0"
                        }
                      }}
                    />
                  );
                })}
              </Box>
            </Box>
          )}

          {/* Modal Filter Tabs (Only for Total Projects Modal) */}
          {cardModalType === "total" && (
            <Tabs
              value={modalTab}
              onChange={(e, val) => setModalTab(val)}
              sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}
            >
              <Tab label={`Active Projects (${metrics.totalActiveProjects !== undefined ? metrics.totalActiveProjects : projectSummary.length})`} sx={{ fontWeight: 600, textTransform: "none" }} />
              <Tab label={`Completed Projects (${metrics.totalCompletedProjects !== undefined ? metrics.totalCompletedProjects : completedProjects.length})`} sx={{ fontWeight: 600, textTransform: "none" }} />
              <Tab label={`All Projects (${metrics.totalAllProjects !== undefined ? metrics.totalAllProjects : (projectSummary.length + completedProjects.length)})`} sx={{ fontWeight: 600, textTransform: "none" }} />
            </Tabs>
          )}

          {/* Table inside Modal */}
          <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid #e2e8f0", borderRadius: "8px" }}>
            {cardModalType === "npi" ? (
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#f8fafc" }}>
                    <TableCell sx={{ fontWeight: 600, color: "#64748b" }}>Title</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: "#64748b" }}>Category</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: "#64748b" }}>Priority</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: "#64748b" }}>Strategic Alignment</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: "#64748b" }}>Risk Level</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: "#64748b" }}>Estimated Budget</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: "#64748b" }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: "#64748b", minWidth: 200 }}>Status Justification / Reason</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: "#64748b" }}>Submitted By</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600, color: "#64748b" }}>AI Recommendation</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: "#64748b" }} align="right">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(() => {
                    const currentFilteredNpiList = (npiOverview.npiList || []).filter(n => {
                      if (selectedNpiStatusFilter === "All") return true;
                      return normalizeNpiStatus(n.status) === selectedNpiStatusFilter;
                    });

                    if (currentFilteredNpiList.length === 0) {
                      return (
                        <TableRow>
                          <TableCell colSpan={11} align="center" sx={{ py: 3, color: "#64748b" }}>
                            No NPI Proposals found {selectedNpiStatusFilter !== "All" ? `with status "${selectedNpiStatusFilter}"` : ""}.
                          </TableCell>
                        </TableRow>
                      );
                    }

                    return currentFilteredNpiList.map((npi) => (
                      <TableRow key={npi.id} hover>
                        <TableCell sx={{ fontWeight: 600, color: "#334155" }}>{npi.title}</TableCell>
                        <TableCell sx={{ color: "#64748b" }}>{npi.category}</TableCell>
                        <TableCell>
                          <Chip
                            label={npi.priority}
                            size="small"
                            sx={{
                              fontWeight: 600,
                              fontSize: "0.75rem",
                              backgroundColor: npi.priority === "Critical" || npi.priority === "High" ? "#fee2e2" : npi.priority === "Medium" ? "#fef3c7" : "#e0f2fe",
                              color: npi.priority === "Critical" || npi.priority === "High" ? "#b91c1c" : npi.priority === "Medium" ? "#b45309" : "#0369a1",
                              borderRadius: "6px"
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, color: "#334155" }}>{npi.alignment}/10</TableCell>
                        <TableCell>
                          <Chip
                            label={npi.risk}
                            size="small"
                            sx={{
                              fontWeight: 600,
                              fontSize: "0.75rem",
                              backgroundColor: npi.risk === "High" ? "#fee2e2" : npi.risk === "Medium" ? "#fef3c7" : "#dcfce7",
                              color: npi.risk === "High" ? "#b91c1c" : npi.risk === "Medium" ? "#b45309" : "#15803d",
                              borderRadius: "6px"
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, color: "#0f172a" }}>
                          {companyConfig?.currency_symbol || '₹'}{Number(npi.budget).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={npi.status}
                            size="small"
                            sx={{
                              fontWeight: 600,
                              fontSize: "0.75rem",
                              backgroundColor: npi.status === "Approved" || npi.status === "Converted" ? "#dcfce7" : npi.status === "Rejected" ? "#fee2e2" : "#f1f5f9",
                              color: npi.status === "Approved" || npi.status === "Converted" ? "#15803d" : npi.status === "Rejected" ? "#b91c1c" : "#475569",
                              borderRadius: "6px"
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ minWidth: 200, maxWidth: 300 }}>
                          {npi.status_change_reason ? (
                            <Box
                              sx={{
                                bgcolor: "#f8fafc",
                                borderLeft: "3px solid #6366f1",
                                p: 1,
                                borderRadius: "4px"
                              }}
                            >
                              <Typography
                                variant="caption"
                                sx={{
                                  color: "#1e293b",
                                  fontStyle: "italic",
                                  fontWeight: 500,
                                  display: "block",
                                  lineHeight: 1.35
                                }}
                              >
                                "{npi.status_change_reason}"
                              </Typography>
                            </Box>
                          ) : (
                            <Typography variant="caption" sx={{ color: "#94a3b8", fontStyle: "italic" }}>
                              No justification recorded
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ color: "#334155", fontWeight: 600, fontSize: "0.85rem", whiteSpace: "nowrap" }}>
                          {npi.submittedBy || "Team Member"}
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="AI Resource & Feasibility Recommendation">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleTriggerIndividualAI(npi.id, npi.title);
                              }}
                              sx={{ bgcolor: "#ede9fe", color: "#7c3aed", "&:hover": { bgcolor: "#ddd6fe" } }}
                            >
                              <Sparkles size={15} />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            variant="contained"
                            startIcon={<Eye size={13} />}
                            onClick={() => {
                              setModalOpen(false);
                              navigate(`/npi-intake?openId=${npi.id}`, { state: { openIntakeId: npi.id } });
                            }}
                            sx={{
                              borderRadius: 1.5,
                              textTransform: "none",
                              fontWeight: 600,
                              fontSize: "0.75rem",
                              bgcolor: "#2563eb",
                              "&:hover": { bgcolor: "#1d4ed8" },
                              whiteSpace: "nowrap"
                            }}
                          >
                            View Details
                          </Button>
                        </TableCell>
                      </TableRow>
                    ));
                  })()}
                </TableBody>
              </Table>
            ) : cardModalType === "issues" ? (
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#f8fafc" }}>
                    <TableCell sx={{ fontWeight: 600, color: "#64748b" }}>Issue Key</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: "#64748b" }}>Title</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: "#64748b" }}>Project</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: "#64748b" }}>Assignee</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: "#64748b" }}>Priority</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: "#64748b" }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: "#64748b" }}>Due Date</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: "#64748b" }} align="right">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {activeSprintIssues.map((issue) => (
                    <TableRow key={issue.id} hover>
                      <TableCell sx={{ fontWeight: 600, color: "#334155" }}>{issue.key}</TableCell>
                      <TableCell sx={{ fontWeight: 500, color: "#0f172a" }}>{issue.summary}</TableCell>
                      <TableCell sx={{ color: "#64748b" }}>{issue.projectName}</TableCell>
                      <TableCell sx={{ color: "#334155" }}>{issue.assignee}</TableCell>
                      <TableCell>
                        <Chip
                          label={issue.priority}
                          size="small"
                          sx={{
                            fontWeight: 600,
                            fontSize: "0.75rem",
                            backgroundColor: issue.priority === "High" || issue.priority === "Urgent" ? "#fee2e2" : issue.priority === "Medium" ? "#fef3c7" : "#e0f2fe",
                            color: issue.priority === "High" || issue.priority === "Urgent" ? "#b91c1c" : issue.priority === "Medium" ? "#b45309" : "#0369a1",
                            borderRadius: "6px"
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={issue.status}
                          size="small"
                          sx={{
                            fontWeight: 600,
                            fontSize: "0.75rem",
                            backgroundColor: issue.status === "Open" ? "#fee2e2" : issue.status === "In Progress" ? "#dbeafe" : issue.status === "On Hold" ? "#fef3c7" : "#dcfce7",
                            color: issue.status === "Open" ? "#b91c1c" : issue.status === "In Progress" ? "#1d4ed8" : issue.status === "On Hold" ? "#b45309" : "#15803d",
                            borderRadius: "6px"
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ color: "#64748b" }}>{formatDate(issue.dueDate)}</TableCell>
                      <TableCell align="right">
                        <Box display="flex" gap={1} justifyContent="flex-end">
                          <Button
                            size="small"
                            variant="contained"
                            startIcon={<Sparkles size={13} />}
                            onClick={() => {
                              setModalOpen(false);
                              handleOpenTaskMitigation(issue);
                            }}
                            sx={{
                              borderRadius: 1.5,
                              textTransform: "none",
                              fontWeight: 600,
                              fontSize: "0.75rem",
                              backgroundColor: "#2563eb",
                              "&:hover": { backgroundColor: "#1d4ed8" }
                            }}
                          >
                            AI Mitigation
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => {
                              setModalOpen(false);
                              navigate(`/project/${issue.projectId}/db`);
                            }}
                            sx={{ borderRadius: 1.5, textTransform: "none", fontWeight: 600, fontSize: "0.75rem" }}
                          >
                            Project
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                  {activeSprintIssues.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 3, color: "#64748b" }}>
                        No issues found in this sprint.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#f8fafc" }}>
                    <TableCell sx={{ fontWeight: 600, color: "#64748b" }}>Project Code</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: "#64748b" }}>Project Name</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: "#64748b" }}>Program</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: "#64748b" }}>Progress</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: "#64748b" }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: "#64748b" }}>End Date</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: "#64748b" }} align="right">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {((
                    cardModalType === "onTrack" ? onTrackProjectsList :
                    cardModalType === "atRisk" ? atRiskProjectsList :
                    cardModalType === "delayed" ? delayedProjectsList :
                    cardModalType === "progress" ? projectSummary :
                    (modalTab === 0 ? projectSummary : modalTab === 1 ? completedProjects : (allProjects.length > 0 ? allProjects : [...projectSummary, ...completedProjects]))
                  ) || []).map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell sx={{ fontWeight: 600, color: "#334155" }}>{row.key}</TableCell>
                      <TableCell sx={{ fontWeight: 500, color: "#0f172a" }}>{row.name}</TableCell>
                      <TableCell sx={{ color: "#64748b" }}>{row.program}</TableCell>
                      <TableCell sx={{ width: "20%" }}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <LinearProgress
                            variant="determinate"
                            value={row.overallProgress}
                            sx={{
                              flexGrow: 1,
                              height: 6,
                              borderRadius: 3,
                              backgroundColor: "#e2e8f0",
                              "& .MuiLinearProgress-bar": {
                                backgroundColor: row.status === "Completed" ? "#15803d" : row.status === "On Track" ? "#10b981" : row.status === "At Risk" ? "#f59e0b" : "#ef4444"
                              }
                            }}
                          />
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{row.overallProgress}%</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={row.status}
                          size="small"
                          sx={{
                            fontWeight: 600,
                            fontSize: "0.75rem",
                            backgroundColor: row.status === "Completed" ? "#dcfce7" : row.status === "On Track" ? "#dcfce7" : row.status === "At Risk" ? "#fef3c7" : "#fee2e2",
                            color: row.status === "Completed" ? "#15803d" : row.status === "On Track" ? "#15803d" : row.status === "At Risk" ? "#b45309" : "#b91c1c",
                            borderRadius: "6px"
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ color: "#64748b" }}>{formatDate(row.plannedEndDate)}</TableCell>
                      <TableCell align="right">
                        {row.status === "At Risk" || row.status === "Delayed" ? (
                          <Button
                            size="small"
                            variant="contained"
                            startIcon={<Sparkles size={13} />}
                            onClick={() => handleOpenProjectMitigation(row)}
                            sx={{
                              borderRadius: 1.5,
                              textTransform: "none",
                              fontWeight: 600,
                              fontSize: "0.75rem",
                              backgroundColor: row.status === "At Risk" ? "#d97706" : "#dc2626",
                              "&:hover": {
                                backgroundColor: row.status === "At Risk" ? "#b45309" : "#b91c1c"
                              }
                            }}
                          >
                            View
                          </Button>
                        ) : (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => {
                              setModalOpen(false);
                              navigate(`/project/${row.id || row.project_id}/db`);
                            }}
                            sx={{ borderRadius: 1.5, textTransform: "none", fontWeight: 600, fontSize: "0.75rem" }}
                          >
                            View
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {((
                    cardModalType === "onTrack" ? onTrackProjectsList :
                    cardModalType === "atRisk" ? atRiskProjectsList :
                    cardModalType === "delayed" ? delayedProjectsList :
                    cardModalType === "progress" ? projectSummary :
                    (modalTab === 0 ? projectSummary : modalTab === 1 ? completedProjects : (allProjects.length > 0 ? allProjects : [...projectSummary, ...completedProjects]))
                  ) || []).length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 3, color: "#64748b" }}>
                        No projects found for this category.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            )}
            </TableContainer>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setModalOpen(false)} variant="contained" sx={{ textTransform: "none", backgroundColor: "#2563eb" }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {taskDetailsPanelOpen && selectedTaskForPanel && (
        <TaskDetailsPanel
          issue={selectedTaskForPanel}
          onClose={() => setTaskDetailsPanelOpen(false)}
          onUpdate={fetchDashboardData}
          issueTypes={issueTypes}
          statuses={statuses}
          priorities={priorities}
          getInitials={(name) => {
            if (!name) return "UN";
            return name
              .split(" ")
              .map((word) => word[0])
              .join("")
              .toUpperCase();
          }}
        />
      )}

      {/* Portfolio AI Report Modal */}
      <Dialog
        open={portfolioModalOpen}
        onClose={() => setPortfolioModalOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: "16px" } }}
      >
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", bgcolor: "#f8fafc", p: 2.5 }}>
          <Box display="flex" alignItems="center">
            <Sparkles size={20} style={{ color: "#2563eb", marginRight: "8px" }} />
            <Typography variant="h6" sx={{ fontWeight: 700, color: "#1e3a8a" }}>
              NPI Portfolio AI Strategic Analysis
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => setPortfolioModalOpen(false)}>
            <X size={18} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3, bgcolor: "#ffffff" }}>
          {portfolioAiError && (
            <Alert
              severity={portfolioAiError.severity || "error"}
              sx={{ mb: 2, borderRadius: "10px", alignItems: "center" }}
              action={
                portfolioAiError.type === "upgrade" && (
                  <Button
                    color="inherit"
                    size="small"
                    variant="outlined"
                    onClick={() => window.dispatchEvent(new CustomEvent("openUpgradeModal", { detail: { feature: "AI NPI Strategic Analysis" } }))}
                    sx={{ textTransform: "none", fontWeight: 700 }}
                  >
                    Upgrade Plan
                  </Button>
                )
              }
            >
              {portfolioAiError.message}
            </Alert>
          )}
          {portfolioAiLoading ? (
            <Box display="flex" flexDirection="column" alignItems="center" py={6}>
              <CircularProgress size={40} style={{ color: "#2563eb" }} />
              <Typography variant="body2" sx={{ mt: 2, color: "#64748b", fontWeight: 500 }}>
                AI Strategy Agent is analyzing portfolio NPIs...
              </Typography>
            </Box>
          ) : portfolioAiData ? (
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#2563eb", mb: 1, textTransform: "uppercase", letterSpacing: 0.5 }}>
                1. Executive Summary
              </Typography>
              <Typography variant="body2" sx={{ color: "#334155", mb: 3, lineHeight: 1.6 }}>
                {portfolioAiData.portfolio_summary}
              </Typography>

              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#2563eb", mb: 1, textTransform: "uppercase", letterSpacing: 0.5 }}>
                2. Strategic Alignment Analysis
              </Typography>
              <Typography variant="body2" sx={{ color: "#334155", mb: 3, lineHeight: 1.6 }}>
                {portfolioAiData.strategic_alignment_analysis}
              </Typography>

              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#2563eb", mb: 1, textTransform: "uppercase", letterSpacing: 0.5 }}>
                3. Budget & Financial Pipeline Insights
              </Typography>
              <Typography variant="body2" sx={{ color: "#334155", mb: 3, lineHeight: 1.6 }}>
                {portfolioAiData.budget_utilization_insights}
              </Typography>

              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#2563eb", mb: 1, textTransform: "uppercase", letterSpacing: 0.5 }}>
                4. Portfolio Skill/Resource Demand Insights
              </Typography>
              <Typography variant="body2" sx={{ color: "#334155", mb: 3, lineHeight: 1.6 }}>
                {portfolioAiData.skill_demand_analysis}
              </Typography>

              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#2563eb", mb: 1.5, textTransform: "uppercase", letterSpacing: 0.5 }}>
                5. Strategic Priority Rankings
              </Typography>
              {portfolioAiData.priority_ranking?.map((item, idx) => (
                <Box key={idx} sx={{ p: 2, bgcolor: "#f8fafc", borderRadius: "8px", borderLeft: "4px solid #2563eb", mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#1e293b" }}>
                    {idx + 1}. {item.title} <span style={{ fontWeight: 500, color: "#64748b", fontSize: "0.8rem", marginLeft: "8px" }}>Priority: {item.priority_score || "N/A"}</span>
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#475569", mt: 0.5, fontSize: "0.85rem", lineHeight: 1.5 }}>
                    {item.reason}
                  </Typography>
                </Box>
              ))}
            </Box>
          ) : (
            <Typography variant="body2" color="textSecondary" align="center">
              No analysis data available.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2.5, bgcolor: "#f8fafc", borderTop: "1px solid #e2e8f0" }}>
          {portfolioAiData && (
            <Button
              variant="outlined"
              color="primary"
              disabled={exportLoading}
              startIcon={exportLoading ? <CircularProgress size={16} /> : <Download size={16} />}
              onClick={handleExportPortfolioDocx}
              sx={{ textTransform: "none", borderRadius: "8px", fontWeight: 600, mr: "auto" }}
            >
              {exportLoading ? "Exporting..." : "Export to Word (.docx)"}
            </Button>
          )}
          <Button
            onClick={() => setPortfolioModalOpen(false)}
            variant="contained"
            sx={{ textTransform: "none", borderRadius: "8px", fontWeight: 600, bgcolor: "#2563eb", "&:hover": { bgcolor: "#1d4ed8" } }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Individual NPI AI Resource Recommendations Modal */}
      <Dialog
        open={individualModalOpen}
        onClose={() => setIndividualModalOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: "16px" } }}
      >
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", bgcolor: "#f8fafc", p: 2.5 }}>
          <Box display="flex" alignItems="center">
            <Sparkles size={20} style={{ color: "#2563eb", marginRight: "8px" }} />
            <Typography variant="h6" sx={{ fontWeight: 700, color: "#1e3a8a" }}>
              AI Resource Recommendations: {npiAiTitle}
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => setIndividualModalOpen(false)}>
            <X size={18} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3, bgcolor: "#ffffff" }}>
          {individualAiError && (
            <Alert
              severity={individualAiError.severity || "error"}
              sx={{ mb: 2, borderRadius: "10px", alignItems: "center" }}
              action={
                individualAiError.type === "upgrade" && (
                  <Button
                    color="inherit"
                    size="small"
                    variant="outlined"
                    onClick={() => window.dispatchEvent(new CustomEvent("openUpgradeModal", { detail: { feature: "AI NPI Resource Matching" } }))}
                    sx={{ textTransform: "none", fontWeight: 700 }}
                  >
                    Upgrade Plan
                  </Button>
                )
              }
            >
              {individualAiError.message}
            </Alert>
          )}
          {individualAiLoading ? (
            <Box display="flex" flexDirection="column" alignItems="center" py={6}>
              <CircularProgress size={40} style={{ color: "#2563eb" }} />
              <Typography variant="body2" sx={{ mt: 2, color: "#64748b", fontWeight: 500 }}>
                AI Resource Planner is calculating candidate matchings...
              </Typography>
            </Box>
          ) : individualAiData ? (
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#2563eb", mb: 1, textTransform: "uppercase", letterSpacing: 0.5 }}>
                Strategic Fit & Budget Check
              </Typography>
              <Typography variant="body2" sx={{ color: "#334155", mb: 3, lineHeight: 1.6 }}>
                {individualAiData.strategic_fit_analysis}
              </Typography>

              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#2563eb", mb: 1, textTransform: "uppercase", letterSpacing: 0.5 }}>
                Resource Readiness Summary
              </Typography>
              <Typography variant="body2" sx={{ color: "#334155", mb: 3, lineHeight: 1.6 }}>
                {individualAiData.resource_readiness_summary}
              </Typography>

              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#2563eb", mb: 2, textTransform: "uppercase", letterSpacing: 0.5 }}>
                Recommended Resource Allocations
              </Typography>
              
              {individualAiData.recommendations?.map((roleGroup, groupIdx) => (
                <Box key={groupIdx} sx={{ mb: 4 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#0f172a", borderBottom: "2px solid #f1f5f9", pb: 1, mb: 2 }}>
                    Role: {roleGroup.role} <span style={{ fontWeight: 500, color: "#64748b", fontSize: "0.85rem" }}>(Requested: {roleGroup.requested_count})</span>
                  </Typography>

                  {roleGroup.recommended_employees?.length > 0 ? (
                    <Grid container spacing={2.5}>
                      {roleGroup.recommended_employees.map((rec, recIdx) => (
                        <Grid item xs={12} sm={6} key={recIdx}>
                          <Paper
                            variant="outlined"
                            sx={{
                              p: 2.5,
                              borderRadius: "12px",
                              borderColor: "#e2e8f0",
                              transition: "all 0.2s",
                              "&:hover": { borderColor: "#3b82f6", boxShadow: "0 4px 12px rgba(59,130,246,0.05)" }
                            }}
                          >
                            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1.5}>
                              <Box>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#1e293b" }}>
                                  {rec.name}
                                </Typography>
                                <Typography variant="caption" sx={{ color: "#64748b" }}>
                                  ID: {rec.employee_id}
                                </Typography>
                              </Box>
                              <Chip
                                label={`${rec.match_percentage}% Match`}
                                size="small"
                                sx={{
                                  fontWeight: 700,
                                  fontSize: "0.75rem",
                                  bgcolor: rec.match_percentage >= 85 ? "#dcfce7" : rec.match_percentage >= 60 ? "#fef3c7" : "#fee2e2",
                                  color: rec.match_percentage >= 85 ? "#15803d" : rec.match_percentage >= 60 ? "#b45309" : "#b91c1c"
                                }}
                              />
                            </Box>

                            <Box mb={1.5}>
                              <Typography variant="caption" sx={{ fontWeight: 600, color: "#64748b", textTransform: "uppercase" }}>
                                Matching Skills
                              </Typography>
                              <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.5}>
                                {rec.matching_skills?.map((skill, sIdx) => (
                                  <Chip
                                    key={sIdx}
                                    label={skill}
                                    size="small"
                                    sx={{ fontSize: "0.7rem", height: 20, bgcolor: "#f1f5f9", color: "#334155" }}
                                  />
                                ))}
                              </Box>
                            </Box>

                            <Box mb={1.5}>
                              <Typography variant="caption" sx={{ fontWeight: 600, color: "#64748b", textTransform: "uppercase" }}>
                                Allocation / KT Recommendation
                              </Typography>
                              <Typography variant="body2" sx={{ fontWeight: 700, color: rec.allocation_suggestion?.toLowerCase().includes("kt") || rec.allocation_suggestion?.toLowerCase().includes("knowledge") ? "#b45309" : "#10b981", mt: 0.5 }}>
                                {rec.allocation_suggestion}
                              </Typography>
                            </Box>

                            <Box>
                              <Typography variant="caption" sx={{ fontWeight: 600, color: "#64748b", textTransform: "uppercase" }}>
                                Fit Rationale
                              </Typography>
                              <Typography variant="body2" sx={{ color: "#475569", mt: 0.5, fontSize: "0.8rem", lineHeight: 1.4 }}>
                                {rec.match_reason}
                              </Typography>
                            </Box>
                          </Paper>
                        </Grid>
                      ))}
                    </Grid>
                  ) : (
                    <Typography variant="body2" sx={{ color: "#dc2626", fontStyle: "italic" }}>
                      ⚠️ No suitable candidates found for this role.
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          ) : (
            <Typography variant="body2" color="textSecondary" align="center">
              No matching recommendations available.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2.5, bgcolor: "#f8fafc", borderTop: "1px solid #e2e8f0" }}>
          <Button
            onClick={() => setIndividualModalOpen(false)}
            variant="contained"
            sx={{ textTransform: "none", borderRadius: "8px", fontWeight: 600, bgcolor: "#2563eb", "&:hover": { bgcolor: "#1d4ed8" } }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* AI Risk Mitigation & Action Plan Modal */}
      <Dialog
        open={aiMitigationModalOpen}
        onClose={() => setAiMitigationModalOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "16px",
            boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
            overflow: "hidden"
          }
        }}
      >
        {/* Dialog Header */}
        <Box
          sx={{
            p: 2.5,
            background: selectedProjectForMitigation?.status === "Delayed"
              ? "linear-gradient(135deg, #7f1d1d 0%, #991b1b 50%, #dc2626 100%)"
              : "linear-gradient(135deg, #78350f 0%, #92400e 50%, #d97706 100%)",
            color: "#ffffff",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center"
          }}
        >
          <Box display="flex" alignItems="center" gap={1.5}>
            <Box
              sx={{
                backgroundColor: "rgba(255, 255, 255, 0.2)",
                p: 1,
                borderRadius: "10px",
                display: "flex",
                alignItems: "center",
                backdropFilter: "blur(4px)"
              }}
            >
              <Sparkles size={22} color="#ffffff" />
            </Box>
            <Box>
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="h6" sx={{ fontWeight: 700, fontSize: "1.1rem", color: "#ffffff" }}>
                  AI Risk Mitigation & Action Plan
                </Typography>
                <Chip
                  label={selectedProjectForMitigation?.status || "At Risk"}
                  size="small"
                  sx={{
                    backgroundColor: "rgba(255, 255, 255, 0.25)",
                    color: "#ffffff",
                    fontWeight: 700,
                    fontSize: "0.75rem",
                    borderRadius: "6px"
                  }}
                />
              </Box>
              <Typography variant="caption" sx={{ color: "rgba(255, 255, 255, 0.9)", fontSize: "0.8rem" }}>
                {selectedProjectForMitigation?.key} • {selectedProjectForMitigation?.name} ({selectedProjectForMitigation?.overallProgress}% Completed)
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={() => setAiMitigationModalOpen(false)} sx={{ color: "#ffffff" }}>
            <X size={20} />
          </IconButton>
        </Box>

        <DialogContent sx={{ p: 3, backgroundColor: "#f8fafc" }}>
          {/* 1. Project & Reported Root Cause Banner */}
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              mb: 3,
              borderRadius: "12px",
              backgroundColor: "#ffffff",
              border: selectedProjectForMitigation?.status === "Delayed" ? "1px solid #fecaca" : "1px solid #fde68a",
              boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.05)"
            }}
          >
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1.5}>
              <Box display="flex" alignItems="center" gap={1}>
                <AlertTriangle
                  size={18}
                  color={selectedProjectForMitigation?.status === "Delayed" ? "#dc2626" : "#d97706"}
                />
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 700,
                    color: selectedProjectForMitigation?.status === "Delayed" ? "#991b1b" : "#92400e",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    fontSize: "0.75rem"
                  }}
                >
                  Reported Reason & Root Cause
                </Typography>
              </Box>
              <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600 }}>
                Planned End Date: {formatDate(selectedProjectForMitigation?.plannedEndDate)}
              </Typography>
            </Box>

            {/* Root cause text */}
            <Box
              sx={{
                p: 2,
                borderRadius: "8px",
                backgroundColor: selectedProjectForMitigation?.status === "Delayed" ? "#fef2f2" : "#fffbeb",
                border: selectedProjectForMitigation?.status === "Delayed" ? "1px dashed #fca5a5" : "1px dashed #fcd34d",
                color: "#1e293b",
                fontSize: "0.9rem",
                lineHeight: 1.6,
                fontWeight: 500
              }}
            >
              {selectedProjectForMitigation?.reason || aiMitigationData?.reason || "No custom reason recorded yet by the project team. AI is analyzing status, timeline variance, and progress metrics."}
            </Box>

            {/* Progress & Timeline Bar */}
            <Box mt={2} display="flex" alignItems="center" gap={2}>
              <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600, minWidth: 110 }}>
                Current Progress:
              </Typography>
              <LinearProgress
                variant="determinate"
                value={Number(selectedProjectForMitigation?.overallProgress) || 0}
                sx={{
                  flexGrow: 1,
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: "#e2e8f0",
                  "& .MuiLinearProgress-bar": {
                    backgroundColor: selectedProjectForMitigation?.status === "Delayed" ? "#dc2626" : "#d97706"
                  }
                }}
              />
              <Typography variant="body2" sx={{ fontWeight: 700, minWidth: 35, color: "#1e293b" }}>
                {selectedProjectForMitigation?.overallProgress || 0}%
              </Typography>
            </Box>
          </Paper>

          {/* 2. Loading State */}
          {aiMitigationLoading && (
            <Box
              py={6}
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              gap={2}
            >
              <CircularProgress size={45} thickness={4} sx={{ color: selectedProjectForMitigation?.status === "Delayed" ? "#dc2626" : "#d97706" }} />
              <Box textAlign="center">
                <Typography variant="body1" sx={{ fontWeight: 600, color: "#1e293b" }}>
                 AI is analyzing root cause and generating mitigation plan & action items...
                </Typography>
                <Typography variant="caption" sx={{ color: "#64748b" }}>
                  Synthesizing root cause impediment, timeline variance, and role ownership
                </Typography>
              </Box>
            </Box>
          )}

          {/* 3. Error State */}
          {aiMitigationError && !aiMitigationLoading && (
            <Alert
              severity={typeof aiMitigationError === "object" ? aiMitigationError.severity : "error"}
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => {
                    if (typeof aiMitigationError === "object" && aiMitigationError.type === "upgrade") {
                      window.dispatchEvent(new CustomEvent("openUpgradeModal", { detail: { feature: "AI Project Risk Mitigation" } }));
                    } else {
                      handleOpenProjectMitigation(selectedProjectForMitigation);
                    }
                  }}
                  sx={{ fontWeight: 700 }}
                >
                  {typeof aiMitigationError === "object" && aiMitigationError.type === "upgrade" ? "Upgrade Plan" : "Retry"}
                </Button>
              }
              sx={{ mb: 2 }}
            >
              {typeof aiMitigationError === "object" ? aiMitigationError.message : aiMitigationError}
            </Alert>
          )}

          {/* 4. AI Generated Results */}
          {aiMitigationData && !aiMitigationLoading && (
            <Box display="flex" flexDirection="column" gap={2.5}>
              {/* Strategic Mitigation Plan */}
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  borderRadius: "12px",
                  backgroundColor: "#ffffff",
                  border: "1px solid #e2e8f0"
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#0f172a", fontSize: "0.9rem", mb: 1.5 }}>
                  🛡️ Strategic Mitigation Plan
                </Typography>
                <Box display="flex" flexDirection="column" gap={1.2}>
                  {aiMitigationData.mitigationPlan?.map((step, idx) => (
                    <Box
                      key={idx}
                      display="flex"
                      alignItems="flex-start"
                      gap={1.5}
                      sx={{
                        p: 1.5,
                        borderRadius: "8px",
                        backgroundColor: "#f8fafc",
                        border: "1px solid #f1f5f9"
                      }}
                    >
                      <Box
                        sx={{
                          minWidth: 24,
                          height: 24,
                          borderRadius: "50%",
                          backgroundColor: "#eff6ff",
                          color: "#2563eb",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontWeight: 700,
                          fontSize: "0.75rem"
                        }}
                      >
                        {idx + 1}
                      </Box>
                      <Typography variant="body2" sx={{ color: "#334155", fontWeight: 500, fontSize: "0.85rem", pt: 0.2 }}>
                        {step}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Paper>

              {/* Concrete Action Items Table */}
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  borderRadius: "12px",
                  backgroundColor: "#ffffff",
                  border: "1px solid #e2e8f0"
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#0f172a", fontSize: "0.9rem", mb: 1.5 }}>
                  🎯 Action Items
                </Typography>
                <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid #e2e8f0", borderRadius: "8px" }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: "#f8fafc" }}>
                        <TableCell sx={{ fontWeight: 600, color: "#64748b", fontSize: "0.75rem" }}>Action Item</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: "#64748b", fontSize: "0.75rem" }}>Owner</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: "#64748b", fontSize: "0.75rem" }}>Timeline</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: "#64748b", fontSize: "0.75rem" }}>Expected Impact</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {aiMitigationData.actionItems?.map((item, idx) => (
                        <TableRow key={idx} hover>
                          <TableCell sx={{ fontWeight: 600, color: "#1e293b", fontSize: "0.8rem" }}>
                            {item.action}
                          </TableCell>
                          <TableCell sx={{ color: "#334155", fontSize: "0.8rem", minWidth: 160 }}>
                            <Chip
                              label={item.owner || "Team Lead"}
                              size="small"
                              sx={{
                                fontSize: "0.75rem",
                                backgroundColor: "#eff6ff",
                                color: "#1d4ed8",
                                fontWeight: 600,
                                height: "auto",
                                py: 0.5,
                                px: 0.5,
                                "& .MuiChip-label": {
                                  whiteSpace: "normal",
                                  display: "block"
                                }
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ color: selectedProjectForMitigation?.status === "Delayed" ? "#dc2626" : "#d97706", fontWeight: 600, fontSize: "0.75rem" }}>
                            {item.timeline || "Immediate"}
                          </TableCell>
                          <TableCell sx={{ color: "#64748b", fontSize: "0.8rem" }}>
                            {item.impact || "Unblocks project progress"}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>

             
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2.5, backgroundColor: "#ffffff", borderTop: "1px solid #e2e8f0", justifyContent: "space-between" }}>
          <Button
            size="small"
            startIcon={<Sparkles size={16} />}
            onClick={() => handleOpenProjectMitigation(selectedProjectForMitigation)}
            disabled={aiMitigationLoading}
            sx={{ textTransform: "none", fontWeight: 600, color: selectedProjectForMitigation?.status === "Delayed" ? "#dc2626" : "#d97706" }}
          >
            Regenerate AI Plan
          </Button>
          <Box display="flex" gap={1.5}>
            <Button
              variant="outlined"
              onClick={() => {
                setAiMitigationModalOpen(false);
                navigate(`/project/${selectedProjectForMitigation?.id || selectedProjectForMitigation?.project_id}/db`);
              }}
              sx={{ textTransform: "none", fontWeight: 600 }}
            >
              Open Project Workspace
            </Button>
            <Button
              variant="contained"
              onClick={() => setAiMitigationModalOpen(false)}
              sx={{ textTransform: "none", fontWeight: 600, backgroundColor: "#2563eb" }}
            >
              Done
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Task / Issue Level AI Risk Mitigation & Action Items Modal */}
      <Dialog
        open={taskAiMitigationModalOpen}
        onClose={() => setTaskAiMitigationModalOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: "16px", overflow: "hidden" }
        }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            px: 3,
            py: 2.5,
            backgroundColor: "#2563eb",
            color: "#ffffff"
          }}
        >
          <Box display="flex" alignItems="center" gap={1.5}>
            <Box
              sx={{
                p: 1,
                borderRadius: "10px",
                backgroundColor: "rgba(255, 255, 255, 0.2)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
              }}
            >
              <Sparkles size={22} color="#ffffff" />
            </Box>
            <Box>
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: "#ffffff", fontSize: "1.1rem" }}>
                  Task AI Risk Mitigation & Action Plan
                </Typography>
                <Chip
                  label={selectedTaskForMitigation?.taskId || selectedTaskForMitigation?.key || "Task"}
                  size="small"
                  sx={{
                    backgroundColor: "rgba(255, 255, 255, 0.25)",
                    color: "#ffffff",
                    fontWeight: 700,
                    fontSize: "0.75rem"
                  }}
                />
              </Box>
              <Typography variant="caption" sx={{ color: "rgba(255, 255, 255, 0.9)", fontSize: "0.8rem" }}>
                {selectedTaskForMitigation?.taskName || selectedTaskForMitigation?.summary || "Overdue Task Item"} • {selectedTaskForMitigation?.projectName || selectedTaskForMitigation?.project || "Project"}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={() => setTaskAiMitigationModalOpen(false)} sx={{ color: "#ffffff" }}>
            <X size={20} />
          </IconButton>
        </Box>

        <DialogContent sx={{ p: 3, backgroundColor: "#f8fafc" }}>
          {/* 1. Task Metadata & Reported Risk Reason Banner */}
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              mb: 3,
              borderRadius: "12px",
              backgroundColor: "#ffffff",
              border: "1px solid #fed7aa",
              boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.05)"
            }}
          >
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1.5}>
              <Box display="flex" alignItems="center" gap={1}>
                <AlertTriangle size={18} color="#d97706" />
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 700,
                    color: "#9a3412",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    fontSize: "0.75rem"
                  }}
                >
                  Reported Reason & Root Cause
                </Typography>
              </Box>
              <Typography variant="caption" sx={{ color: "#dc2626", fontWeight: 700 }}>
                {selectedTaskForMitigation?.daysOverdue ? `${selectedTaskForMitigation.daysOverdue} Days Overdue` : "Needs Immediate Resolution"}
              </Typography>
            </Box>

            {/* Root cause text */}
            <Box
              sx={{
                p: 2,
                borderRadius: "8px",
                backgroundColor: "#fff7ed",
                border: "1px dashed #fdba74",
                color: "#1e293b",
                fontSize: "0.9rem",
                lineHeight: 1.6,
                fontWeight: 500
              }}
            >
              {selectedTaskForMitigation?.reason || taskAiMitigationData?.reason || "Milestone technical dependencies and pending validation blocking completion."}
            </Box>

            {/* Task Assignee & Due Date Meta Info */}
            <Box mt={2} display="flex" flexWrap="wrap" gap={2} alignItems="center">
              <Box display="flex" alignItems="center" gap={0.8}>
                <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600 }}>
                  Assignee:
                </Typography>
                <Chip
                  label={taskAiMitigationData?.assignee || selectedTaskForMitigation?.assignee || "Unassigned"}
                  size="small"
                  sx={{
                    backgroundColor: "#eff6ff",
                    color: "#1d4ed8",
                    fontWeight: 600,
                    fontSize: "0.75rem"
                  }}
                />
              </Box>
              <Divider orientation="vertical" flexItem sx={{ height: 16, my: "auto" }} />
              <Box display="flex" alignItems="center" gap={0.8}>
                <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600 }}>
                  Due Date:
                </Typography>
                <Typography variant="caption" sx={{ color: "#334155", fontWeight: 700 }}>
                  {formatDate(selectedTaskForMitigation?.dueDate)}
                </Typography>
              </Box>
              <Divider orientation="vertical" flexItem sx={{ height: 16, my: "auto" }} />
              <Box display="flex" alignItems="center" gap={0.8}>
                <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600 }}>
                  Project:
                </Typography>
                <Typography variant="caption" sx={{ color: "#334155", fontWeight: 700 }}>
                  {selectedTaskForMitigation?.projectName || selectedTaskForMitigation?.project || "Project"}
                </Typography>
              </Box>
            </Box>
          </Paper>

          {/* 2. Loading State */}
          {taskAiMitigationLoading && (
            <Box
              py={6}
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              gap={2}
            >
              <CircularProgress size={45} thickness={4} sx={{ color: "#2563eb" }} />
              <Box textAlign="center">
                <Typography variant="body1" sx={{ fontWeight: 600, color: "#1e293b" }}>
                  AI is analyzing root cause and generating task mitigation plan & action items...
                </Typography>
                <Typography variant="caption" sx={{ color: "#64748b" }}>
                  Synthesizing task impediment, active assignee, and team role ownership
                </Typography>
              </Box>
            </Box>
          )}

          {/* 3. Error State */}
          {taskAiMitigationError && !taskAiMitigationLoading && (
            <Alert
              severity={typeof taskAiMitigationError === "object" ? taskAiMitigationError.severity : "error"}
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => {
                    if (typeof taskAiMitigationError === "object" && taskAiMitigationError.type === "upgrade") {
                      window.dispatchEvent(new CustomEvent("openUpgradeModal", { detail: { feature: "AI Task Risk Mitigation" } }));
                    } else {
                      handleOpenTaskMitigation(selectedTaskForMitigation);
                    }
                  }}
                  sx={{ fontWeight: 700 }}
                >
                  {typeof taskAiMitigationError === "object" && taskAiMitigationError.type === "upgrade" ? "Upgrade Plan" : "Retry"}
                </Button>
              }
              sx={{ mb: 2 }}
            >
              {typeof taskAiMitigationError === "object" ? taskAiMitigationError.message : taskAiMitigationError}
            </Alert>
          )}

          {/* 4. AI Generated Results */}
          {taskAiMitigationData && !taskAiMitigationLoading && (
            <Box display="flex" flexDirection="column" gap={2.5}>
              {/* Tactical Mitigation Plan */}
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  borderRadius: "12px",
                  backgroundColor: "#ffffff",
                  border: "1px solid #e2e8f0"
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#0f172a", fontSize: "0.9rem", mb: 1.5 }}>
                  🛡️ Tactical Mitigation Plan
                </Typography>
                <Box display="flex" flexDirection="column" gap={1.2}>
                  {taskAiMitigationData.mitigationPlan?.map((step, idx) => (
                    <Box
                      key={idx}
                      display="flex"
                      alignItems="flex-start"
                      gap={1.5}
                      sx={{
                        p: 1.5,
                        borderRadius: "8px",
                        backgroundColor: "#f8fafc",
                        border: "1px solid #f1f5f9"
                      }}
                    >
                      <Box
                        sx={{
                          minWidth: 24,
                          height: 24,
                          borderRadius: "50%",
                          backgroundColor: "#eff6ff",
                          color: "#2563eb",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontWeight: 700,
                          fontSize: "0.75rem"
                        }}
                      >
                        {idx + 1}
                      </Box>
                      <Typography variant="body2" sx={{ color: "#334155", fontWeight: 500, fontSize: "0.85rem", pt: 0.2 }}>
                        {step}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Paper>

              {/* Concrete Action Items Table */}
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  borderRadius: "12px",
                  backgroundColor: "#ffffff",
                  border: "1px solid #e2e8f0"
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#0f172a", fontSize: "0.9rem", mb: 1.5 }}>
                  🎯 Action Items
                </Typography>
                <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid #e2e8f0", borderRadius: "8px" }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: "#f8fafc" }}>
                        <TableCell sx={{ fontWeight: 600, color: "#64748b", fontSize: "0.75rem" }}>Action Item</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: "#64748b", fontSize: "0.75rem" }}>Owner</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: "#64748b", fontSize: "0.75rem" }}>Timeline</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: "#64748b", fontSize: "0.75rem" }}>Expected Impact</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {taskAiMitigationData.actionItems?.map((item, idx) => (
                        <TableRow key={idx} hover>
                          <TableCell sx={{ fontWeight: 600, color: "#1e293b", fontSize: "0.8rem" }}>
                            {item.action}
                          </TableCell>
                          <TableCell sx={{ color: "#334155", fontSize: "0.8rem", minWidth: 160 }}>
                            <Chip
                              label={item.owner || selectedTaskForMitigation?.assignee || "Task Owner"}
                              size="small"
                              sx={{
                                fontSize: "0.75rem",
                                backgroundColor: "#eff6ff",
                                color: "#1d4ed8",
                                fontWeight: 600,
                                height: "auto",
                                py: 0.5,
                                px: 0.5,
                                "& .MuiChip-label": {
                                  whiteSpace: "normal",
                                  display: "block"
                                }
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ color: "#d97706", fontWeight: 600, fontSize: "0.75rem" }}>
                            {item.timeline || "Immediate"}
                          </TableCell>
                          <TableCell sx={{ color: "#64748b", fontSize: "0.8rem" }}>
                            {item.impact || "Unblocks task completion"}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>

           
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2.5, backgroundColor: "#ffffff", borderTop: "1px solid #e2e8f0", justifyContent: "space-between" }}>
          <Button
            size="small"
            startIcon={<Sparkles size={16} />}
            onClick={() => handleOpenTaskMitigation(selectedTaskForMitigation)}
            disabled={taskAiMitigationLoading}
            sx={{ textTransform: "none", fontWeight: 600, color: "#2563eb" }}
          >
            Regenerate AI Plan
          </Button>
          <Button
            variant="contained"
            onClick={() => setTaskAiMitigationModalOpen(false)}
            sx={{ textTransform: "none", fontWeight: 600, backgroundColor: "#2563eb" }}
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default CpoPpmDashboard;

